import React, { useState, useEffect } from 'react';
import {
  Lock, User, Mail, ShieldCheck, AlertTriangle,
  CheckCircle, RefreshCcw, Smartphone, ShieldAlert,
  KeyRound, Calculator, Eye, EyeOff, UserCheck, FileText, MapPin,
  Search, Shield, BookOpen, Download, X, ChevronLeft, Loader, ZoomIn,
  Globe, Key, AtSign, UserSearch, Camera, FileSearch, MapPin as MapPinIcon, Info
} from 'lucide-react';

/**
 * CIBERBIT - FRONTEND V13.0
 * Dark cybersecurity aesthetic — cyan/neon accents
 * Fix: Word/Excel viewer via PDF export proxy
 */

const APPS_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbwdj5pwxopr4BNbszKFIbBi797NrVDbLWr9IkLI3DEEiWWKNFVixX8JnWyrjFtc3o3h/exec";
const DORKING_URL     = "https://script.google.com/macros/s/AKfycbwgHDURbX9H0Z-ZL-FkZE8GqvwvxIxMWZC_HfYkGXrRqkVLWoYiNIUMmAKNWkBDeYGJ9Q/exec";
const ACTIVIDADES_URL = "https://script.google.com/macros/s/AKfycbwMkJxd9hMxh0e4K6TzKlFSbGAwocaF4mB9aHsmGBEnIrn28AJRRlB1D3HXFZLvk6wfcw/exec";
const HERRAMIENTAS_URL = "https://script.google.com/macros/s/AKfycbxTDLHD-780aDQ-GOkDyL7wTrTdPnpZYH-2vCP6v6h6TtaC4eLeGcVm8un5ed2tDiax/exec";

// ── ESTILOS GLOBALES ──────────────────────────────────────────
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&family=Rajdhani:wght@400;600;700&family=Orbitron:wght@700;900&display=swap');
  * { box-sizing: border-box; }
  body { background: #080c10; color: #e2e8f0; font-family: 'Rajdhani', sans-serif; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #0d1117; }
  ::-webkit-scrollbar-thumb { background: #00f5d4; border-radius: 3px; }
  @keyframes scanline {
    0% { transform: translateY(-100%); }
    100% { transform: translateY(100vh); }
  }
  @keyframes flicker { 0%,100%{opacity:1} 92%{opacity:1} 93%{opacity:.8} 95%{opacity:1} }
  @keyframes glow { 0%,100%{text-shadow:0 0 8px #00f5d4,0 0 16px #00f5d4} 50%{text-shadow:0 0 20px #00f5d4,0 0 40px #00f5d4,0 0 60px #00b4d8} }
  @keyframes pulse-border { 0%,100%{box-shadow:0 0 0 0 rgba(0,245,212,0.4)} 50%{box-shadow:0 0 0 8px rgba(0,245,212,0)} }
  @keyframes matrix-fall { 0%{transform:translateY(-20px);opacity:0} 10%{opacity:1} 90%{opacity:1} 100%{transform:translateY(20px);opacity:0} }
  .font-mono-cyber { font-family:'Share Tech Mono',monospace; }
  .font-orbitron { font-family:'Orbitron',sans-serif; }
  .font-rajdhani { font-family:'Rajdhani',sans-serif; }
  .glow-cyan { animation: glow 3s ease-in-out infinite; }
  .flicker { animation: flicker 8s linear infinite; }
  .pulse-border { animation: pulse-border 2s ease-in-out infinite; }
  .cyber-grid {
    background-image:
      linear-gradient(rgba(0,245,212,0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,245,212,0.03) 1px, transparent 1px);
    background-size: 40px 40px;
  }
  .neon-line { height:1px; background: linear-gradient(90deg, transparent, #00f5d4, transparent); }
  .hex-clip { clip-path: polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%); }
`;

// ── LOGO SHIELD CIRCUITO ──────────────────────────────────────
const CiberBitLogo = ({ size = 80 }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* Shield base */}
    <path d="M50 8 L88 24 L88 52 C88 72 70 88 50 95 C30 88 12 72 12 52 L12 24 Z"
      fill="#0d1117" stroke="#00f5d4" strokeWidth="2.5"/>
    {/* Circuit lines */}
    <line x1="30" y1="40" x2="45" y2="40" stroke="#00f5d4" strokeWidth="1.5"/>
    <line x1="45" y1="40" x2="45" y2="30" stroke="#00f5d4" strokeWidth="1.5"/>
    <line x1="45" y1="30" x2="60" y2="30" stroke="#00f5d4" strokeWidth="1.5"/>
    <line x1="55" y1="40" x2="70" y2="40" stroke="#00f5d4" strokeWidth="1.5"/>
    <line x1="55" y1="40" x2="55" y2="55" stroke="#00f5d4" strokeWidth="1.5"/>
    <line x1="35" y1="55" x2="55" y2="55" stroke="#00f5d4" strokeWidth="1.5"/>
    <line x1="35" y1="55" x2="35" y2="68" stroke="#00f5d4" strokeWidth="1.5"/>
    <line x1="35" y1="68" x2="65" y2="68" stroke="#00f5d4" strokeWidth="1.5"/>
    <line x1="65" y1="55" x2="65" y2="68" stroke="#00f5d4" strokeWidth="1.5"/>
    <line x1="65" y1="40" x2="65" y2="55" stroke="#00f5d4" strokeWidth="1.5"/>
    {/* Nodes */}
    <circle cx="30" cy="40" r="3" fill="#00f5d4"/>
    <circle cx="45" cy="30" r="3" fill="#00f5d4"/>
    <circle cx="60" cy="30" r="3" fill="#00f5d4" opacity="0.5"/>
    <circle cx="70" cy="40" r="3" fill="#00f5d4"/>
    <circle cx="55" cy="55" r="3" fill="#00f5d4"/>
    <circle cx="35" cy="68" r="3" fill="#00f5d4"/>
    <circle cx="65" cy="68" r="3" fill="#00f5d4"/>
    <circle cx="50" cy="50" r="5" fill="#00f5d4" opacity="0.9"/>
    <circle cx="50" cy="50" r="8" fill="none" stroke="#00f5d4" strokeWidth="1" opacity="0.4"/>
  </svg>
);

// ── HOOK TIMEOUT ──────────────────────────────────────────────
const useSessionTimeout = (isActive, onTimeout, timeoutMs = 180000) => {
  useEffect(() => {
    if (!isActive) return;
    let timer;
    const reset = () => { clearTimeout(timer); timer = setTimeout(onTimeout, timeoutMs); };
    ['mousemove','keypress','click','scroll'].forEach(e => window.addEventListener(e, reset));
    reset();
    return () => {
      clearTimeout(timer);
      ['mousemove','keypress','click','scroll'].forEach(e => window.removeEventListener(e, reset));
    };
  }, [isActive, onTimeout, timeoutMs]);
};

// ── UTILIDADES ────────────────────────────────────────────────
const sanitizeInput = (str) => typeof str !== 'string' ? str : str.replace(/[<>]/g,'').trim();

const getClientMetadata = async () => {
  let ip = "0.0.0.0";
  const to = (url) => Promise.race([fetch(url), new Promise((_,r)=>setTimeout(()=>r(new Error),5000))]);
  try { const r=await to('https://api.ipify.org?format=json'); if(r.ok){const d=await r.json();if(d.ip)ip=String(d.ip);else throw 0;}else throw 0; }
  catch { try { const r=await to('https://ipwho.is/'); if(r.ok){const d=await r.json();if(d.success&&d.ip)ip=String(d.ip);else throw 0;}else throw 0; }
  catch { try { const r=await to('https://api.db-ip.com/v2/free/self'); if(r.ok){const d=await r.json();if(d.ipAddress)ip=String(d.ipAddress);}else throw 0; } catch{ip="0.0.0.0";} } }
  return { ip, userAgent: navigator.userAgent, lat:"N/A", lng:"N/A" };
};

const checkLocation = async () => true;

const formatSize = (b) => {
  if (!b) return '—';
  if (b<1024) return b+' B';
  if (b<1024*1024) return (b/1024).toFixed(1)+' KB';
  return (b/(1024*1024)).toFixed(1)+' MB';
};

// ── VISOR URL ─────────────────────────────────────────────────
// Word/Excel/PPT → se piden como PDF al backend (GAS los exporta)
// PDF e imágenes → preview embed de Drive
const getViewerUrl = (fileId, mime) => {
  if (mime.startsWith('image/')) return `https://drive.google.com/file/d/${fileId}/preview`;
  if (mime === 'application/pdf') return `https://drive.google.com/file/d/${fileId}/preview`;
  if (mime.includes('google-apps')) {
    if (mime.includes('document'))     return `https://docs.google.com/document/d/${fileId}/preview`;
    if (mime.includes('spreadsheet'))  return `https://docs.google.com/spreadsheets/d/${fileId}/preview`;
    if (mime.includes('presentation')) return `https://docs.google.com/presentation/d/${fileId}/preview`;
  }
  // Word/Excel/PPT → solicitar versión PDF al backend
  return null; // señal para pedir exportPDF
};

const needsPdfExport = (mime) =>
  mime.includes('wordprocessingml') ||
  mime.includes('spreadsheetml') ||
  mime.includes('presentationml') ||
  mime.includes('msword') ||
  mime.includes('ms-excel');

// ── COMPONENTES UI CYBER ──────────────────────────────────────
const CyberButton = ({ children, onClick, variant='primary', className='', type='button', disabled=false }) => {
  const base = "relative px-6 py-3 font-bold transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transform active:scale-95 uppercase tracking-widest text-[11px] cursor-pointer select-none overflow-hidden font-rajdhani";
  const vs = {
    primary:   "bg-cyan-500/10 border border-cyan-500 text-cyan-300 hover:bg-cyan-500/20 hover:text-white hover:shadow-[0_0_20px_rgba(0,245,212,0.3)]",
    secondary: "bg-transparent border border-gray-600 text-gray-400 hover:border-gray-400 hover:text-white",
    danger:    "bg-red-500/10 border border-red-500 text-red-400 hover:bg-red-500/20 hover:shadow-[0_0_20px_rgba(239,68,68,0.3)]",
    ghost:     "bg-white/5 border border-white/10 text-white/60 hover:bg-white/10 hover:text-white",
  };
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${vs[variant]||vs.primary} ${className}`}>
      <span className="relative z-10 flex items-center gap-2">{children}</span>
    </button>
  );
};

const CyberInput = ({ label, icon: Icon, type='text', onChange, preventPaste=false, ...props }) => {
  const [show, setShow] = useState(false);
  const isPass = type==='password';
  const handle = (e) => {
    if(e.target.value.startsWith(' ')) e.target.value=e.target.value.trimStart();
    if(type==='email') e.target.value=e.target.value.toLowerCase();
    if(onChange) onChange(e);
  };
  return (
    <div className="mb-4 w-full text-left">
      <label className="block text-[10px] font-bold uppercase text-cyan-500/70 tracking-[0.2em] mb-1.5 font-mono-cyber">{label}</label>
      <div className="relative">
        {Icon && <div className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-500/40"><Icon size={16}/></div>}
        <input {...props}
          type={isPass?(show?'text':'password'):type}
          onChange={handle}
          onPaste={(e)=>preventPaste&&e.preventDefault()}
          className="w-full bg-gray-900/80 border border-gray-700 focus:border-cyan-500 text-white placeholder-gray-600 rounded-none p-3 pl-11 pr-10 outline-none transition-all font-mono-cyber text-sm focus:shadow-[0_0_15px_rgba(0,245,212,0.15)] focus:bg-gray-900"
          style={{clipPath:'polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,0 100%)'}}
        />
        {isPass && <button type="button" onClick={()=>setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-cyan-400"><Eye size={16}/></button>}
      </div>
    </div>
  );
};

const CyberSelect = ({ label, icon: Icon, options, ...props }) => (
  <div className="mb-4 w-full text-left">
    <label className="block text-[10px] font-bold uppercase text-cyan-500/70 tracking-[0.2em] mb-1.5 font-mono-cyber">{label}</label>
    <div className="relative">
      {Icon && <div className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-500/40"><Icon size={16}/></div>}
      <select {...props}
        className="w-full bg-gray-900/80 border border-gray-700 focus:border-cyan-500 text-white rounded-none p-3 pl-11 outline-none appearance-none font-mono-cyber text-sm transition-all"
        style={{clipPath:'polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,0 100%)'}}>
        {options.map(o=><option key={o} value={o} className="bg-gray-900">{o}</option>)}
      </select>
    </div>
  </div>
);

const CyberCard = ({ children, title, className='' }) => (
  <div className={`bg-gray-900/90 border border-gray-700/50 relative ${className}`}
    style={{clipPath:'polygon(0 0,calc(100% - 16px) 0,100% 16px,100% 100%,0 100%)'}}>
    <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-60"/>
    {title && (
      <div className="px-8 pt-8 pb-2">
        <h2 className="font-orbitron text-xl font-black text-white uppercase tracking-widest flex items-center gap-3">
          <span className="text-cyan-400">//</span>{title}
        </h2>
        <div className="neon-line mt-3 opacity-30"/>
      </div>
    )}
    <div className="p-8 pt-4">{children}</div>
  </div>
);

// ── MODAL TÉRMINOS ────────────────────────────────────────────
const TermsModal = ({ onClose, onAccept }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/90 backdrop-blur-sm">
    <div className="bg-gray-900 border border-cyan-500/30 max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl shadow-cyan-500/10"
      style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,0 100%)'}}>
      <div className="p-8 border-b border-gray-800">
        <h2 className="font-orbitron text-xl font-black text-cyan-400 uppercase tracking-widest">Términos y Ley 1581</h2>
      </div>
      <div className="overflow-y-auto flex-1 p-8 text-xs text-gray-400 space-y-4 text-justify font-rajdhani leading-relaxed">
        <p><strong className="text-cyan-400">1. ACEPTACIÓN:</strong> Al registrarse, acepta las condiciones de uso del portal CiberBit.</p>
        <p><strong className="text-cyan-400">2. LEY 1581 DE 2012:</strong> El usuario autoriza a CiberBit para el tratamiento de sus datos personales de conformidad con la Ley Estatutaria 1581 de 2012.</p>
        <p><strong className="text-cyan-400">3. FINALIDAD:</strong> Autenticación 2FA, seguridad ISO 27001, Rate Limiting, fines comerciales y facturación.</p>
        <p><strong className="text-cyan-400">4. DERECHOS:</strong> Puede conocer, actualizar, rectificar y suprimir sus datos enviando solicitud por canales oficiales.</p>
        <p><strong className="text-cyan-400">5. SEGURIDAD:</strong> Datos protegidos con criptografía avanzada (TLS 1.3, HMAC-SHA256).</p>
      </div>
      <div className="p-8 border-t border-gray-800 flex gap-4">
        <CyberButton onClick={onClose} variant="secondary" className="flex-1">Cerrar</CyberButton>
        <CyberButton onClick={onAccept} className="flex-1">Aceptar Políticas</CyberButton>
      </div>
    </div>
  </div>
);

// ── 2FA ───────────────────────────────────────────────────────
const TwoFactorAuth = ({ email, currentIp, onVerified, onCancel, onLog }) => {
  const [code,    setCode]    = useState("");
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");
  const [msg,     setMsg]     = useState("");

  const handleVerify = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const res  = await fetch(APPS_SCRIPT_URL, { method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'},
        body: new URLSearchParams({action:'loginVerify2FA',email,code,clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"}).toString() });
      const data = JSON.parse(await res.text());
      if (data.result==='success') { onVerified({...data.user,logs:data.logs||[]}); if(onLog)onLog(email,"LOGIN_SUCCESS"); }
      else { setError(data.message||"Código incorrecto"); if(onLog)onLog(email,"LOGIN_FAIL_2FA"); }
    } catch { setError("Error de conexión"); } finally { setLoading(false); }
  };

  const sendEmailCode = async () => {
    setLoading(true); setMsg(""); setError("");
    try {
      const res  = await fetch(APPS_SCRIPT_URL, { method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'},
        body: new URLSearchParams({action:'send2FAEmail',email,clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"}).toString() });
      const data = JSON.parse(await res.text());
      if(data.result==='success') setMsg("Código enviado."); else setError(data.message||"Error.");
    } catch { setError("Sin conexión."); } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gray-950 cyber-grid flex items-center justify-center p-6">
      <style>{GLOBAL_STYLES}</style>
      <div className="max-w-md w-full bg-gray-900/95 border border-cyan-500/40 shadow-2xl shadow-cyan-500/20 relative"
        style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,20px 100%,0 calc(100% - 20px))'}}>
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent"/>
        <div className="p-10 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 border-2 border-cyan-500 flex items-center justify-center pulse-border bg-cyan-500/5">
              <ShieldCheck size={36} className="text-cyan-400"/>
            </div>
          </div>
          <h2 className="font-orbitron text-xl font-black text-white uppercase tracking-widest mb-1">Autenticación 2FA</h2>
          <p className="text-xs text-gray-500 font-mono-cyber mb-8 uppercase tracking-widest">Microsoft Authenticator</p>
          <form onSubmit={handleVerify} className="space-y-4">
            <input type="text" value={code} onChange={(e)=>setCode(e.target.value.replace(/[^0-9]/g,'').slice(0,6))}
              className="w-full text-center text-4xl font-mono-cyber tracking-[0.5em] bg-transparent border-b-2 border-cyan-500 text-cyan-300 outline-none py-4 focus:border-cyan-300 transition-colors"
              placeholder="000000" autoFocus/>
            {error && <p className="text-red-400 text-[10px] font-mono-cyber uppercase animate-bounce">{error}</p>}
            {msg   && <p className="text-green-400 text-[10px] font-mono-cyber uppercase">{msg}</p>}
            <CyberButton type="submit" className="w-full py-4" disabled={loading}>{loading?'Verificando...':'Validar Acceso'}</CyberButton>
          </form>
          <div className="mt-8 pt-6 border-t border-gray-800">
            <button onClick={sendEmailCode} className="text-[10px] font-mono-cyber text-gray-500 hover:text-cyan-400 uppercase underline block w-full mb-4">Enviar código al correo</button>
            <button onClick={onCancel} className="text-[10px] font-mono-cyber text-red-500 hover:text-red-400 uppercase">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── SETUP 2FA ─────────────────────────────────────────────────
const TwoFactorSetup = ({ secret, email, onContinue }) => {
  const qrUrl = `https://quickchart.io/qr?text=${encodeURIComponent(`otpauth://totp/CiberBit:${email}?secret=${secret}&issuer=CiberBit`)}&size=300`;
  return (
    <CyberCard title="Configurar 2FA" className="text-center">
      <div className="flex flex-col items-center space-y-6">
        <img src={qrUrl} alt="QR" className="w-48 h-48 border border-cyan-500/30 p-2"/>
        <div className="text-left space-y-2 max-w-xs mx-auto">
          <p className="text-[10px] font-mono-cyber uppercase text-cyan-500/60">Instrucciones:</p>
          <ol className="text-xs text-gray-400 list-decimal pl-4 space-y-1">
            <li>Descarga <strong className="text-white">Microsoft Authenticator</strong>.</li>
            <li>Agregar cuenta → Otra → Escanea el QR.</li>
          </ol>
        </div>
        <div className="bg-black/50 border border-gray-700 p-4 w-full">
          <p className="text-[10px] font-mono-cyber text-cyan-500/60 uppercase mb-1">Clave Manual</p>
          <p className="font-mono-cyber text-cyan-300 text-sm tracking-widest break-all select-all">{secret}</p>
        </div>
        <CyberButton onClick={onContinue} className="w-full">Ya configuré el autenticador</CyberButton>
      </div>
    </CyberCard>
  );
};

// ── FILE ICON ─────────────────────────────────────────────────
const FileIcon = ({ mime, size=40 }) => {
  const s = { fontSize: size };
  if (mime==='application/pdf')                   return <div style={s}>📄</div>;
  if (mime.startsWith('image/'))                  return <div style={s}>🖼️</div>;
  if (mime.includes('sheet')||mime.includes('excel')) return <div style={s}>📊</div>;
  if (mime.includes('word')||mime.includes('document')) return <div style={s}>📝</div>;
  return <div style={s}>📁</div>;
};

// ── VISOR DE ARCHIVO ──────────────────────────────────────────
const FileViewer = ({ file, onClose, onDownload, downloading, actividadesToken, currentIp }) => {
  const [iframeLoading, setIframeLoading] = useState(true);
  const [pdfUrl,        setPdfUrl]        = useState(null);
  const [pdfError,      setPdfError]      = useState('');

  const requiresExport = needsPdfExport(file.mime);
  const directUrl      = !requiresExport ? getViewerUrl(file.id, file.mime) : null;

  useEffect(() => {
    if (!requiresExport) return;
    // Pedir al backend que exporte Word/Excel como PDF
    setIframeLoading(true);
    fetch(ACTIVIDADES_URL, {
      method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'},
      body: new URLSearchParams({action:'exportPDF', token:actividadesToken, fileId:file.id, clientIP:currentIp}).toString()
    })
    .then(r=>r.text()).then(t=>{
      const d = JSON.parse(t);
      if (d.result !== 'success') { setPdfError(d.message||'No se pudo convertir.'); return; }
      if (d.mode === 'preview') {
        setPdfUrl(d.pdfUrl);
      } else if (d.mode === 'base64') {
        try {
          const binary = atob(d.pdfBase64);
          const bytes  = new Uint8Array(binary.length);
          for (let i=0; i<binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const blob = new Blob([bytes], {type:'application/pdf'});
          setPdfUrl(URL.createObjectURL(blob));
        } catch(e) { setPdfError('Error al procesar el PDF.'); }
      }
    })
    .catch(()=>setPdfError('Error de conexión al convertir.'))
    .finally(()=>setIframeLoading(false));
  }, [file.id]);

  const viewUrl = requiresExport ? pdfUrl : directUrl;

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-gray-950">
      <style>{GLOBAL_STYLES}</style>
      {/* NAVBAR VISOR */}
      <div className="bg-black border-b border-cyan-500/20 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <FileIcon mime={file.mime} size={20}/>
          <div className="min-w-0">
            <p className="text-white font-bold text-sm truncate font-rajdhani">{file.name}</p>
            <p className="text-cyan-500/50 text-[10px] font-mono-cyber uppercase">{formatSize(file.size)} · Solo lectura</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-4">
          <div className="hidden md:flex items-center gap-2 bg-amber-900/20 border border-amber-500/30 px-3 py-1.5">
            <Shield size={11} className="text-amber-400"/>
            <span className="text-amber-300 text-[10px] font-mono-cyber uppercase">Solo lectura</span>
          </div>
          <CyberButton onClick={()=>onDownload(file)} disabled={downloading===file.id} className="py-2 px-4">
            {downloading===file.id?<Loader size={12} className="animate-spin"/>:<Download size={12}/>} Descargar
          </CyberButton>
          <CyberButton onClick={onClose} variant="ghost" className="py-2 px-4">
            <X size={14}/> Cerrar
          </CyberButton>
        </div>
      </div>
      {/* VISOR */}
      <div className="flex-1 relative bg-gray-900">
        {iframeLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10 cyber-grid">
            <Loader size={40} className="text-cyan-400 animate-spin mb-4"/>
            <p className="text-cyan-500/60 font-mono-cyber uppercase text-sm tracking-widest">
              {requiresExport ? 'Convirtiendo a PDF...' : 'Cargando visor...'}
            </p>
          </div>
        )}
        {pdfError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
            <AlertTriangle size={48} className="text-red-400 mb-4"/>
            <p className="text-red-400 font-mono-cyber text-sm uppercase mb-4">{pdfError}</p>
            <CyberButton onClick={onClose} variant="ghost">Volver</CyberButton>
          </div>
        )}
        {viewUrl && !pdfError && (
          <iframe src={viewUrl} title={file.name} className="w-full h-full border-0"
            onLoad={()=>setIframeLoading(false)}
            sandbox="allow-scripts allow-same-origin allow-popups"
            allow="fullscreen"/>
        )}
      </div>
    </div>
  );
};

// ── MÓDULO ACTIVIDADES ────────────────────────────────────────
const ActividadesModule = ({ user, currentIp, onClose, actividadesToken }) => {
  const [files,       setFiles]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [downloading, setDownloading] = useState(null);
  const [viewing,     setViewing]     = useState(null);

  useEffect(()=>{ loadFiles(); },[]);

  const loadFiles = async () => {
    setLoading(true); setError('');
    try {
      const res  = await fetch(ACTIVIDADES_URL, { method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'},
        body: new URLSearchParams({action:'listFiles',token:actividadesToken,clientIP:currentIp}).toString() });
      const data = JSON.parse(await res.text());
      if(data.result==='success') setFiles(data.files||[]);
      else setError(data.message||'Error al cargar.');
    } catch { setError('Error de conexión.'); } finally { setLoading(false); }
  };

  const handleDownload = async (file) => {
    setDownloading(file.id);
    try {
      const res  = await fetch(ACTIVIDADES_URL, { method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'},
        body: new URLSearchParams({action:'getFile',token:actividadesToken,fileId:file.id,clientIP:currentIp}).toString() });
      const data = JSON.parse(await res.text());
      if(data.result==='success'){
        const a=document.createElement('a'); a.href=data.downloadUrl; a.download=data.fileName;
        a.target='_blank'; a.rel='noopener noreferrer';
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
      } else alert(data.message||'Error.');
    } catch { alert('Error de conexión.'); } finally { setDownloading(null); }
  };

  if (viewing) return (
    <FileViewer file={viewing} onClose={()=>setViewing(null)}
      onDownload={handleDownload} downloading={downloading}
      actividadesToken={actividadesToken} currentIp={currentIp}/>
  );

  return (
    <div className="fixed inset-0 z-40 bg-gray-950 flex flex-col cyber-grid">
      <style>{GLOBAL_STYLES}</style>
      {/* NAVBAR */}
      <nav className="bg-black/90 border-b border-cyan-500/20 px-8 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Shield size={22} className="text-cyan-400"/>
          <div>
            <div className="font-orbitron text-base font-black text-white uppercase tracking-widest leading-none">Actividades Ciberseguridad</div>
            <div className="text-cyan-500/50 text-[10px] font-mono-cyber uppercase tracking-widest">Solo lectura — auditado</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 bg-green-900/20 border border-green-500/30 px-3 py-1.5">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"/>
            <span className="text-green-400 text-[10px] font-mono-cyber uppercase">Sesión Activa</span>
          </div>
          <CyberButton onClick={onClose} variant="ghost" className="py-2 px-4">
            <ChevronLeft size={14}/> Volver
          </CyberButton>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-8">
        {/* BANNER */}
        <div className="mb-6 bg-amber-900/10 border border-amber-500/20 px-6 py-3 flex items-center gap-3">
          <Shield size={16} className="text-amber-400 shrink-0"/>
          <p className="text-amber-300/80 text-xs font-mono-cyber uppercase tracking-wide">
            Solo lectura · Clic para visualizar · Descarga disponible · Sin edición · Actividad registrada
          </p>
        </div>

        {loading && (
          <div className="flex flex-col items-center justify-center py-32">
            <Loader size={48} className="text-cyan-400 animate-spin mb-4"/>
            <p className="text-cyan-500/50 font-mono-cyber uppercase text-sm tracking-widest">Cargando archivos...</p>
          </div>
        )}

        {error && !loading && (
          <div className="flex flex-col items-center justify-center py-32">
            <AlertTriangle size={48} className="text-red-400 mb-4"/>
            <p className="text-red-400 font-mono-cyber text-sm uppercase mb-6">{error}</p>
            <CyberButton onClick={loadFiles} variant="ghost">
              <RefreshCcw size={14}/> Reintentar
            </CyberButton>
          </div>
        )}

        {!loading && !error && files.length===0 && (
          <div className="flex flex-col items-center justify-center py-32">
            <BookOpen size={48} className="text-gray-700 mb-4"/>
            <p className="text-gray-600 font-mono-cyber uppercase text-sm">Sin archivos disponibles</p>
          </div>
        )}

        {!loading && !error && files.length>0 && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-orbitron text-lg font-black text-white uppercase tracking-widest">
                <span className="text-cyan-400">{files.length}</span> archivo{files.length!==1?'s':''}
              </h2>
              <span className="text-gray-600 text-[10px] font-mono-cyber uppercase">Clic → visualizar</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {files.map(file=>(
                <div key={file.id}
                  className="bg-gray-900/80 border border-gray-800 hover:border-cyan-500/50 transition-all duration-200 hover:shadow-lg hover:shadow-cyan-500/10 hover:-translate-y-1 group"
                  style={{clipPath:'polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,0 100%)'}}>
                  <div onClick={()=>setViewing(file)}
                    className="aspect-square bg-gray-800/50 flex items-center justify-center overflow-hidden relative cursor-pointer">
                    {file.thumbnail?(
                      <img src={file.thumbnail} alt={file.name} className="w-full h-full object-cover"
                        onError={e=>{e.target.style.display='none';e.target.nextSibling.style.display='flex';}}/>
                    ):null}
                    <div className={`${file.thumbnail?'hidden':'flex'} w-full h-full items-center justify-center`}>
                      <FileIcon mime={file.mime} size={44}/>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-cyan-900/40 transition-all flex items-center justify-center">
                      <div className="opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center gap-1">
                        <ZoomIn size={24} className="text-cyan-300"/>
                        <span className="text-cyan-300 text-[10px] font-mono-cyber uppercase">Ver</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-gray-300 text-[11px] font-bold truncate mb-1 font-rajdhani" title={file.name}>{file.name}</p>
                    <p className="text-gray-600 text-[10px] font-mono-cyber mb-2">{formatSize(file.size)}</p>
                    <div className="flex gap-1.5">
                      <button onClick={()=>setViewing(file)}
                        className="flex-1 py-1.5 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 text-[9px] font-mono-cyber uppercase tracking-wide transition-all cursor-pointer flex items-center justify-center gap-1">
                        <ZoomIn size={10}/> Ver
                      </button>
                      <button onClick={()=>handleDownload(file)} disabled={downloading===file.id}
                        className="flex-1 py-1.5 bg-white/5 hover:bg-white/10 border border-gray-700 text-gray-400 hover:text-white text-[9px] font-mono-cyber uppercase tracking-wide transition-all cursor-pointer flex items-center justify-center gap-1 disabled:opacity-40">
                        {downloading===file.id?<Loader size={10} className="animate-spin"/>:<Download size={10}/>}
                        {downloading===file.id?'...':'Bajar'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── APP PRINCIPAL ─────────────────────────────────────────────

// ── MODAL HERRAMIENTAS ────────────────────────────────────────
const HerramientasModal = ({ tool, onClose, onRun, onCheckPassword, loading, result, error, input, setInput }) => {

  const configs = {
    url: {
      title:       "Analizador URL / IP",
      color:       "green",
      placeholder: "https://ejemplo.com o 192.168.1.1",
      action:      "analyzeURL",
      paramKey:    "target",
      btnText:     "Analizar",
    },
    email: {
      title:       "Verificador de Emails Filtrados",
      color:       "red",
      placeholder: "usuario@dominio.com",
      action:      "checkEmail",
      paramKey:    "targetEmail",
      btnText:     "Verificar Email",
    },
    osint: {
      title:       "OSINT de Usuario",
      color:       "orange",
      placeholder: "nombre_de_usuario",
      action:      "osintUser",
      paramKey:    "username",
      btnText:     "Buscar en todas las plataformas",
    },
    password: {
      title:       "Verificador de Contraseñas Comprometidas",
      color:       "yellow",
      placeholder: "Escribe la contraseña a verificar",
      action:      "checkPassword",
      paramKey:    "password",
      btnText:     "Verificar Contraseña",
    },
  };

  const cfg   = configs[tool] || configs.url;
  const color = cfg.color;

  const colorMap = {
    green:  { border:"border-green-500/40",  text:"text-green-400",  bg:"bg-green-500/10",  hover:"hover:bg-green-500/20",  shadow:"hover:shadow-green-500/20"  },
    red:    { border:"border-red-500/40",    text:"text-red-400",    bg:"bg-red-500/10",    hover:"hover:bg-red-500/20",    shadow:"hover:shadow-red-500/20"    },
    yellow: { border:"border-yellow-500/40", text:"text-yellow-400", bg:"bg-yellow-500/10", hover:"hover:bg-yellow-500/20", shadow:"hover:shadow-yellow-500/20" },
    orange: { border:"border-orange-500/40", text:"text-orange-400", bg:"bg-orange-500/10", hover:"hover:bg-orange-500/20", shadow:"hover:shadow-orange-500/20" },
  };
  const c = colorMap[color] || colorMap.green;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (tool === 'password') { onCheckPassword(input); return; }
    onRun(cfg.action, { action: cfg.action, [cfg.paramKey]: input });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-gray-900 border ${c.border} w-full max-w-2xl max-h-[90vh] flex flex-col shadow-2xl relative`}
        style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,20px 100%,0 calc(100% - 20px))'}}
        onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div className={`border-b border-gray-800 px-6 py-4 flex items-center justify-between`}>
          <h3 className={`font-orbitron text-sm font-black uppercase tracking-widest ${c.text}`}>{cfg.title}</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-white transition-colors cursor-pointer"><X size={18}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Input */}
          <form onSubmit={handleSubmit} className="mb-6">
            <div className="flex gap-3">
              <input
                type={tool==='password'?'password':'text'}
                value={input}
                onChange={e=>setInput(e.target.value)}
                placeholder={cfg.placeholder}
                className={`flex-1 bg-black/50 border border-gray-700 focus:${c.border} text-white placeholder-gray-600 px-4 py-3 outline-none font-mono-cyber text-sm transition-all`}
                style={{clipPath:'polygon(0 0,calc(100% - 8px) 0,100% 8px,100% 100%,0 100%)'}}
              />
              <button type="submit" disabled={loading||!input.trim()}
                className={`px-6 py-3 ${c.bg} ${c.hover} border ${c.border} ${c.text} hover:text-white font-mono-cyber text-[11px] uppercase tracking-widest transition-all cursor-pointer disabled:opacity-40 flex items-center gap-2 shrink-0`}
                style={{clipPath:'polygon(0 0,calc(100% - 8px) 0,100% 8px,100% 100%,0 100%)'}}>
                {loading?<Loader size={14} className="animate-spin"/>:<Search size={14}/>}
                {loading?'Analizando...':cfg.btnText}
              </button>
            </div>
            {tool==='password'&&(
              <p className="text-gray-600 text-[10px] font-mono-cyber uppercase mt-2">
                🔒 La contraseña nunca sale del navegador — solo el hash SHA-1 (k-anonymity)
              </p>
            )}
          </form>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 px-4 py-3 mb-4">
              <p className="text-red-400 text-xs font-mono-cyber uppercase">{error}</p>
            </div>
          )}

          {/* RESULTADOS URL/IP */}
          {result && tool==='url' && (
            <div className="space-y-4">
              <div className={`p-6 border ${result.data.malicious?'border-red-500/40 bg-red-500/5':'border-green-500/40 bg-green-500/5'}`}
                style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`text-3xl font-orbitron font-black ${result.data.malicious?'text-red-400':'text-green-400'}`}>
                    {result.data.malicious ? '⚠️ MALICIOSO' : '✅ LIMPIO'}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center mb-4">
                  {[
                    {label:'Malicioso',  val:result.data.detections,  col:'text-red-400'},
                    {label:'Sospechoso', val:result.data.suspicious,  col:'text-yellow-400'},
                    {label:'Limpio',     val:result.data.harmless,    col:'text-green-400'},
                    {label:'Total',      val:result.data.total,       col:'text-cyan-400'},
                  ].map(s=>(
                    <div key={s.label} className="bg-black/40 border border-gray-800 p-3">
                      <div className={`text-2xl font-orbitron font-black ${s.col}`}>{s.val}</div>
                      <div className="text-gray-600 text-[10px] font-mono-cyber uppercase">{s.label}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-mono-cyber">
                  <div><span className="text-gray-600 uppercase">País: </span><span className="text-white">{result.data.country}</span></div>
                  <div><span className="text-gray-600 uppercase">Reputación: </span><span className="text-white">{result.data.reputation}</span></div>
                  <div><span className="text-gray-600 uppercase">Categorías: </span><span className="text-white">{result.data.categories}</span></div>
                  <div><span className="text-gray-600 uppercase">Último análisis: </span><span className="text-white">{result.data.last_analysis}</span></div>
                </div>
              </div>
              {result.data.engines && result.data.engines.length > 0 && (
                <div>
                  <p className="text-gray-600 text-[10px] font-mono-cyber uppercase mb-2">Motores que detectaron amenaza:</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {result.data.engines.map((e,i)=>(
                      <div key={i} className="flex justify-between items-center bg-black/30 border border-red-500/20 px-3 py-1.5">
                        <span className="text-gray-300 text-xs font-mono-cyber">{e.engine}</span>
                        <span className="text-red-400 text-[10px] font-mono-cyber uppercase">{e.result||e.category}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* RESULTADOS PASSWORD */}
          {result && tool==='password' && (
            <div className={`p-6 border ${result.compromised?'border-red-500/40 bg-red-500/5':'border-green-500/40 bg-green-500/5'}`}
              style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
              <div className={`text-2xl font-orbitron font-black mb-2 ${result.compromised?'text-red-400':'text-green-400'}`}>
                {result.compromised ? '🚨 CONTRASEÑA COMPROMETIDA' : '✅ CONTRASEÑA SEGURA'}
              </div>
              {result.compromised ? (
                <p className="text-red-300 text-sm font-rajdhani">
                  Esta contraseña fue filtrada <strong className="text-red-400 font-orbitron">{result.count.toLocaleString()}</strong> veces en brechas de datos conocidas. Cámbiala inmediatamente.
                </p>
              ) : (
                <p className="text-green-300 text-sm font-rajdhani">Esta contraseña no aparece en ninguna brecha de datos conocida. Sigue siendo una buena práctica cambiarla periódicamente.</p>
              )}
            </div>
          )}

          {/* RESULTADOS EMAIL */}
          {result && tool==='email' && (
            <div className="space-y-3">
              {result.fallback ? (
                <div className="bg-yellow-500/5 border border-yellow-500/30 p-5">
                  <p className="text-yellow-300 text-sm font-rajdhani mb-4">{result.message}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {result.links.map((l,i)=>(
                      <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-black/40 border border-gray-700 hover:border-yellow-500/50 px-4 py-2.5 text-yellow-300 hover:text-white text-xs font-mono-cyber uppercase transition-all">
                        <Globe size={12}/> {l.name}
                      </a>
                    ))}
                  </div>
                </div>
              ) : result.breached === false ? (
                <div className="bg-green-500/5 border border-green-500/40 p-5">
                  <div className="text-green-400 text-xl font-orbitron font-black mb-2">✅ SIN BRECHAS</div>
                  <p className="text-green-300 text-sm font-rajdhani">{result.message}</p>
                </div>
              ) : (
                <>
                  <div className="bg-red-500/5 border border-red-500/40 p-4">
                    <div className="text-red-400 text-xl font-orbitron font-black">🚨 {result.count} BRECHA{result.count!==1?'S':''} ENCONTRADA{result.count!==1?'S':''}</div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {result.breaches.map((b,i)=>(
                      <div key={i} className="bg-black/40 border border-red-500/20 p-4">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-white font-bold font-rajdhani">{b.name}</span>
                          <span className="text-gray-600 text-[10px] font-mono-cyber">{b.date}</span>
                        </div>
                        <div className="text-gray-500 text-[10px] font-mono-cyber uppercase mb-1">
                          {b.count.toLocaleString()} cuentas · {b.domain}
                        </div>
                        <div className="text-cyan-500/70 text-[10px] font-mono-cyber">{b.dataTypes}</div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* RESULTADOS OSINT */}
          {result && tool==='osint' && (
            <div className="space-y-5">
              <div className="bg-orange-500/5 border border-orange-500/30 px-4 py-3">
                <p className="text-orange-300 text-xs font-mono-cyber uppercase">
                  Usuario buscado: <span className="text-white font-bold">{result.username}</span> · {(result.searchEngines.length+result.socialMedia.length+result.technical.length)} plataformas
                </p>
              </div>
              {[
                {title:'Motores de Búsqueda', data:result.searchEngines, color:'text-cyan-400', border:'border-cyan-500/20'},
                {title:'Redes Sociales',      data:result.socialMedia,   color:'text-purple-400', border:'border-purple-500/20'},
                {title:'Plataformas Técnicas',data:result.technical,     color:'text-orange-400', border:'border-orange-500/20'},
              ].map(section=>(
                <div key={section.title}>
                  <p className={`text-[10px] font-mono-cyber uppercase mb-2 ${section.color}`}>{section.title}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {section.data.map((p,i)=>(
                      <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
                        className={`flex items-center gap-2 bg-black/40 border ${section.border} hover:border-gray-500 px-3 py-2 text-gray-400 hover:text-white text-[11px] font-mono-cyber uppercase transition-all`}>
                        <Globe size={11}/> {p.name}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};



// ── ANALIZADOR DE METADATOS ────────────────────────────────────
const MetadatosPanel = ({ onClose }) => {
  const [file,       setFile]       = React.useState(null);
  const [loading,    setLoading]    = React.useState(false);
  const [metadata,   setMetadata]   = React.useState(null);
  const [error,      setError]      = React.useState('');
  const [libLoaded,  setLibLoaded]  = React.useState(false);
  const fileRef = React.useRef();

  // Load ExifReader dynamically
  React.useEffect(() => {
    if (window.ExifReader) { setLibLoaded(true); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/exifreader@4.14.1/dist/exif-reader.js';
    script.onload = () => setLibLoaded(true);
    script.onerror = () => setError('No se pudo cargar la librería de análisis.');
    document.head.appendChild(script);
  }, []);

  const formatGPS = (tags) => {
    try {
      const lat = tags['GPSLatitude']?.description;
      const lng = tags['GPSLongitude']?.description;
      const latRef = tags['GPSLatitudeRef']?.value?.[0] || 'N';
      const lngRef = tags['GPSLongitudeRef']?.value?.[0] || 'E';
      if (!lat || !lng) return null;
      const latNum = parseFloat(lat) * (latRef === 'S' ? -1 : 1);
      const lngNum = parseFloat(lng) * (lngRef === 'W' ? -1 : 1);
      return { lat: latNum, lng: lngNum, display: `${latNum.toFixed(6)}, ${lngNum.toFixed(6)}` };
    } catch { return null; }
  };

  const analyzeFile = async (f) => {
    if (!libLoaded || !window.ExifReader) { setError('Librería aún cargando...'); return; }
    setLoading(true); setError(''); setMetadata(null);
    try {
      const buffer = await f.arrayBuffer();
      const tags   = window.ExifReader.load(buffer, { expanded: true });

      // Flatten all tag groups
      const allTags = {};
      for (const group of Object.values(tags)) {
        if (group && typeof group === 'object') {
          for (const [k, v] of Object.entries(group)) {
            if (k !== 'MakerNote' && v?.description !== undefined) {
              allTags[k] = v.description;
            }
          }
        }
      }

      // Categorize
      const gps = formatGPS(allTags);
      const camera = {};
      const datetime = {};
      const image = {};
      const author = {};
      const software = {};
      const other = {};

      const cameraKeys = ['Make','Model','LensModel','LensMake','FocalLength','ApertureValue','FNumber',
        'ExposureTime','ISOSpeedRatings','Flash','WhiteBalance','ExposureMode','MeteringMode',
        'ExposureProgram','BrightnessValue','ShutterSpeedValue','MaxApertureValue'];
      const dateKeys   = ['DateTimeOriginal','DateTime','DateTimeDigitized','CreateDate','ModifyDate'];
      const imageKeys  = ['ImageWidth','ImageLength','PixelXDimension','PixelYDimension',
        'ColorSpace','Orientation','ResolutionUnit','XResolution','YResolution',
        'BitsPerSample','Compression','PhotometricInterpretation'];
      const authorKeys = ['Artist','Copyright','Creator','Author','XPAuthor','OwnerName'];
      const softKeys   = ['Software','ProcessingSoftware','HostComputer'];
      const gpsSkip    = new Set(['GPSLatitude','GPSLongitude','GPSLatitudeRef','GPSLongitudeRef',
        'GPSAltitude','GPSAltitudeRef','GPSTimeStamp','GPSDateStamp','GPSDOP',
        'GPSImgDirection','GPSImgDirectionRef','GPSSpeed','GPSSpeedRef']);

      for (const [k, v] of Object.entries(allTags)) {
        if (gpsSkip.has(k)) continue;
        if (cameraKeys.includes(k)) camera[k] = v;
        else if (dateKeys.includes(k)) datetime[k] = v;
        else if (imageKeys.includes(k)) image[k] = v;
        else if (authorKeys.includes(k)) author[k] = v;
        else if (softKeys.includes(k)) software[k] = v;
        else other[k] = v;
      }

      setMetadata({ gps, camera, datetime, image, author, software, other,
        filename: f.name, size: f.size, type: f.type, total: Object.keys(allTags).length });
    } catch(e) {
      if (e.message?.includes('No Exif')) {
        setError('Este archivo no contiene metadatos EXIF detectables.');
      } else {
        setError('Error al analizar: ' + e.message);
      }
    } finally { setLoading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); analyzeFile(f); }
  };

  const handleSelect = (e) => {
    const f = e.target.files[0];
    if (f) { setFile(f); analyzeFile(f); }
  };

  const formatSize = (b) => b < 1024*1024 ? (b/1024).toFixed(1)+' KB' : (b/(1024*1024)).toFixed(2)+' MB';

  const MetaGroup = ({ title, color, icon, data }) => {
    if (!data || Object.keys(data).length === 0) return null;
    const colorMap = {
      cyan:   { border:'border-cyan-500/30',   text:'text-cyan-400',   bg:'bg-cyan-500/5'   },
      green:  { border:'border-green-500/30',  text:'text-green-400',  bg:'bg-green-500/5'  },
      red:    { border:'border-red-500/30',     text:'text-red-400',    bg:'bg-red-500/5'    },
      yellow: { border:'border-yellow-500/30', text:'text-yellow-400', bg:'bg-yellow-500/5' },
      purple: { border:'border-purple-500/30', text:'text-purple-400', bg:'bg-purple-500/5' },
      orange: { border:'border-orange-500/30', text:'text-orange-400', bg:'bg-orange-500/5' },
      gray:   { border:'border-gray-700',      text:'text-gray-400',   bg:'bg-gray-800/50'  },
    };
    const c = colorMap[color] || colorMap.gray;
    return (
      <div className={`border ${c.border} ${c.bg} mb-4`}
        style={{clipPath:'polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,0 100%)'}}>
        <div className={`px-4 py-2 border-b ${c.border} flex items-center gap-2`}>
          <span className="text-lg">{icon}</span>
          <span className={`font-mono-cyber text-[10px] font-black uppercase tracking-widest ${c.text}`}>{title}</span>
          <span className={`ml-auto text-[9px] font-mono-cyber ${c.text} opacity-60`}>{Object.keys(data).length} campos</span>
        </div>
        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {Object.entries(data).map(([k,v]) => (
            <div key={k} className="bg-black/30 px-3 py-2 flex flex-col">
              <span className="text-gray-600 text-[9px] font-mono-cyber uppercase tracking-wide">{k}</span>
              <span className="text-white text-[11px] font-rajdhani break-all leading-tight mt-0.5">{String(v)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-950 cyber-grid">
      <style>{GLOBAL_STYLES}</style>
      {/* NAVBAR */}
      <nav className="bg-black/95 border-b border-cyan-500/20 px-6 py-4 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <Camera size={22} className="text-cyan-400"/>
          <div>
            <div className="font-orbitron text-sm font-black text-white uppercase tracking-widest">Analizador de Metadatos</div>
            <div className="text-cyan-500/50 text-[10px] font-mono-cyber uppercase">100% local — el archivo nunca sale del navegador</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 bg-green-900/20 border border-green-500/30 px-3 py-1.5">
            <Shield size={11} className="text-green-400"/>
            <span className="text-green-400 text-[10px] font-mono-cyber uppercase">Procesamiento Local</span>
          </div>
          <button onClick={onClose}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 font-mono-cyber text-[10px] uppercase tracking-widest transition-all cursor-pointer">
            <X size={14}/> Cerrar
          </button>
        </div>
      </nav>

      <div className="flex-1 overflow-y-auto p-6">
        {/* BANNER PRIVACIDAD */}
        <div className="mb-5 bg-green-900/10 border border-green-500/20 px-5 py-3 flex items-center gap-3">
          <Shield size={16} className="text-green-400 shrink-0"/>
          <p className="text-green-300/80 text-xs font-mono-cyber uppercase tracking-wide">
            Privacidad total · El archivo se analiza localmente en tu navegador · Ningun dato sale de tu dispositivo · Compatible con JPG, PNG, WEBP, TIFF, HEIC, RAW y mas
          </p>
        </div>

        {/* DROP ZONE */}
        {!metadata && !loading && (
          <div
            onDrop={handleDrop}
            onDragOver={e=>e.preventDefault()}
            onClick={()=>fileRef.current?.click()}
            className="border-2 border-dashed border-cyan-500/30 hover:border-cyan-500/60 bg-gray-900/40 hover:bg-gray-900/60 transition-all cursor-pointer flex flex-col items-center justify-center py-16 px-8 text-center mb-5"
            style={{clipPath:'polygon(0 0,calc(100% - 16px) 0,100% 16px,100% 100%,16px 100%,0 calc(100% - 16px))'}}>
            <Camera size={52} className="text-cyan-400/40 mb-4"/>
            <p className="font-orbitron text-lg font-black text-white uppercase tracking-widest mb-2">Arrastra tu archivo aquí</p>
            <p className="text-gray-500 font-mono-cyber text-xs uppercase">o haz clic para seleccionar</p>
            <p className="text-gray-600 font-mono-cyber text-[10px] uppercase mt-3">JPG · PNG · WEBP · TIFF · HEIC · RAW · PDF · y más</p>
            <input ref={fileRef} type="file" className="hidden" onChange={handleSelect}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.mp3,.mp4,.mov"/>
          </div>
        )}

        {/* LOADING */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader size={40} className="text-cyan-400 animate-spin mb-4"/>
            <p className="text-cyan-500/60 font-mono-cyber uppercase text-sm tracking-widest">Extrayendo metadatos...</p>
          </div>
        )}

        {/* ERROR */}
        {error && !loading && (
          <div className="bg-red-500/10 border border-red-500/30 px-5 py-4 mb-5 flex items-start gap-3">
            <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5"/>
            <div>
              <p className="text-red-400 font-mono-cyber text-xs uppercase">{error}</p>
              <button onClick={()=>{setError('');setFile(null);setMetadata(null);}}
                className="text-red-400/60 hover:text-red-300 text-[10px] font-mono-cyber uppercase underline mt-2 cursor-pointer">
                Intentar con otro archivo
              </button>
            </div>
          </div>
        )}

        {/* RESULTADOS */}
        {metadata && !loading && (
          <>
            {/* HEADER RESULTADO */}
            <div className="bg-gray-900/80 border border-cyan-500/20 p-5 mb-5 flex flex-wrap items-center gap-4"
              style={{clipPath:'polygon(0 0,calc(100% - 14px) 0,100% 14px,100% 100%,0 100%)'}}>
              <div>
                <p className="font-orbitron text-base font-black text-white truncate max-w-xs">{metadata.filename}</p>
                <p className="text-gray-500 text-[10px] font-mono-cyber uppercase mt-0.5">
                  {formatSize(metadata.size)} · {metadata.type || 'Tipo desconocido'} · <span className="text-cyan-400">{metadata.total} metadatos encontrados</span>
                </p>
              </div>
              <button onClick={()=>{setFile(null);setMetadata(null);setError('');}}
                className="ml-auto bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 px-4 py-2 font-mono-cyber text-[10px] uppercase tracking-widest cursor-pointer transition-all">
                Analizar otro archivo
              </button>
            </div>

            {/* ALERTA GPS */}
            {metadata.gps && (
              <div className="bg-red-900/20 border border-red-500/40 px-5 py-4 mb-5"
                style={{clipPath:'polygon(0 0,calc(100% - 10px) 0,100% 10px,100% 100%,0 100%)'}}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xl">📍</span>
                  <span className="font-mono-cyber text-[11px] font-black text-red-400 uppercase tracking-widest">¡ALERTA DE PRIVACIDAD — COORDENADAS GPS DETECTADAS!</span>
                </div>
                <p className="text-red-300/80 text-xs font-rajdhani mb-3">
                  Este archivo contiene la ubicación exacta donde fue creado. Si compartes este archivo, cualquiera puede saber dónde estabas.
                </p>
                <div className="flex flex-wrap gap-3 items-center">
                  <div className="bg-black/40 border border-red-500/20 px-4 py-2">
                    <span className="text-red-400 font-mono-cyber text-xs">{metadata.gps.display}</span>
                  </div>
                  <a href={`https://www.google.com/maps?q=${metadata.gps.lat},${metadata.gps.lng}`}
                    target="_blank" rel="noopener noreferrer"
                    className="bg-red-500/10 hover:bg-red-500/20 border border-red-500/40 text-red-300 hover:text-white px-4 py-2 font-mono-cyber text-[10px] uppercase tracking-widest transition-all cursor-pointer flex items-center gap-2">
                    <Globe size={12}/> Ver en Google Maps
                  </a>
                </div>
              </div>
            )}

            {/* GRUPOS DE METADATOS */}
            <MetaGroup title="Información de Cámara / Dispositivo" color="cyan"   icon="📷" data={metadata.camera}   />
            <MetaGroup title="Fecha y Hora"                         color="green"  icon="📅" data={metadata.datetime} />
            <MetaGroup title="Dimensiones e Imagen"                 color="purple" icon="🖼️" data={metadata.image}    />
            <MetaGroup title="Autor / Copyright"                    color="yellow" icon="👤" data={metadata.author}   />
            <MetaGroup title="Software Utilizado"                   color="orange" icon="💻" data={metadata.software} />
            {Object.keys(metadata.other).length > 0 && (
              <MetaGroup title="Otros Metadatos" color="gray" icon="🔍" data={metadata.other}/>
            )}
            {!metadata.gps && metadata.total > 0 && (
              <div className="bg-green-900/10 border border-green-500/20 px-5 py-3 flex items-center gap-3 mt-2">
                <Shield size={14} className="text-green-400 shrink-0"/>
                <p className="text-green-300/70 text-xs font-mono-cyber uppercase">Sin datos GPS detectados — este archivo no expone tu ubicacion</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── DATOS DE ATAQUES ──────────────────────────────────────────
const ATTACK_DATA = [
  {
    id: "ddos",
    name: "DDoS",
    fullName: "Distributed Denial of Service",
    color: "red",
    icon: "💥",
    description: "Ataque que busca saturar un servidor, servicio o red con tráfico masivo proveniente de múltiples fuentes distribuidas (botnets), dejándolo inaccesible para usuarios legítimos. Es uno de los ataques más utilizados por grupos hacktivistas, cibercriminales y actores estatales.",
    how: "El atacante controla miles o millones de dispositivos infectados (botnet) sin que sus dueños lo sepan. Todos envían solicitudes simultáneas al objetivo hasta agotar sus recursos de CPU, memoria, ancho de banda o conexiones concurrentes. Los modernos ataques DDoS pueden superar los 3 Tbps de tráfico.",
    phases: [
      "Reconocimiento — el atacante identifica el objetivo y sus capacidades",
      "Construcción de botnet — infecta dispositivos IoT, PCs y servidores vulnerables",
      "Comando y control (C2) — establece canal de comunicación con los bots",
      "Lanzamiento — envía la orden de ataque simultáneo",
      "Amplificación — usa servidores DNS/NTP para multiplicar el tráfico x70",
    ],
    types: [
      "Volumétrico — UDP Flood, ICMP Flood, DNS Amplification (agota ancho de banda)",
      "Protocolo — SYN Flood, Ping of Death, Smurf (agota recursos del servidor)",
      "Capa 7 / Aplicación — HTTP Flood, Slowloris, RUDY (agota conexiones web)",
      "Amplificación DNS — usa resolvers abiertos para multiplicar tráfico x70",
      "Amplificación NTP — usa servidores NTP con monlist para amplificar x556",
      "Carpet Bombing — distribuye el ataque en rangos de IPs completos",
    ],
    tools: ["Mirai Botnet", "LOIC (Low Orbit Ion Cannon)", "HOIC", "Hping3", "MHDDoS", "Slowloris", "GoldenEye"],
    indicators: [
      "Latencia inusualmente alta en el servidor",
      "Tráfico de red anómalo desde múltiples IPs diferentes",
      "CPU y memoria al 100% sin causa aparente",
      "Logs con miles de peticiones por segundo",
      "Caídas del servicio en intervalos regulares",
    ],
    prevention: [
      "CDN con mitigación DDoS activa (Cloudflare, AWS Shield, Akamai)",
      "Rate limiting y filtrado de IP en el firewall perimetral",
      "Anycast network diffusion — distribuye el tráfico globalmente",
      "Listas negras de IPs conocidas maliciosas (Threat Intelligence)",
      "Monitoreo de tráfico en tiempo real con alertas automáticas",
      "Configuración de ACLs para bloquear protocolos de amplificación",
      "Over-provisioning de ancho de banda para absorber picos",
    ],
    realCase: "En febrero 2023, la empresa Cloudflare mitigó el mayor ataque DDoS registrado: 71 millones de solicitudes por segundo provenientes de más de 30,000 direcciones IP. En 2016, la botnet Mirai atacó al proveedor DNS Dyn dejando sin acceso a Twitter, Netflix, Reddit y GitHub durante horas.",
    impact: "Alto — puede dejar servicios críticos completamente inaccesibles. Costo promedio: $120,000 USD por hora de inactividad en empresas medianas.",
    references: [
      { title: "CISA: Understanding and Responding to DDoS Attacks", url: "https://www.cisa.gov/sites/default/files/publications/understanding-and-responding-to-ddos-attacks_508c.pdf" },
      { title: "Cloudflare DDoS Threat Report 2023", url: "https://www.cloudflare.com/learning/ddos/what-is-a-ddos-attack/" },
      { title: "NIST SP 800-61r2 Incident Handling", url: "https://csrc.nist.gov/publications/detail/sp/800-61/rev-2/final" },
      { title: "MITRE ATT&CK: Network Denial of Service T1498", url: "https://attack.mitre.org/techniques/T1498/" },
    ]
  },
  {
    id: "bruteforce",
    name: "Fuerza Bruta",
    fullName: "Brute Force Attack",
    color: "orange",
    icon: "🔨",
    description: "Método de ataque que consiste en probar sistemáticamente todas las combinaciones posibles de credenciales hasta encontrar la correcta. Las herramientas modernas pueden probar miles de millones de combinaciones por segundo usando GPUs. Es especialmente efectivo contra contraseñas débiles, cortas o basadas en palabras del diccionario.",
    how: "El atacante usa herramientas automatizadas que generan y prueban combinaciones usando diccionarios de contraseñas filtradas, variaciones comunes (sustituciones letra→número) y combinaciones puras. Las GPUs modernas permiten hashear hasta 100 billones de contraseñas por segundo con MD5.",
    phases: [
      "Obtención de hashes — el atacante consigue la BD de contraseñas hasheadas (via SQLi, breach, etc)",
      "Selección de modo — diccionario, heurístico o fuerza bruta pura",
      "Procesamiento — GPU computa millones de hashes por segundo",
      "Comparación — compara hashes generados con los objetivo",
      "Uso de credenciales — prueba en servicios online (credential stuffing)",
    ],
    types: [
      "Ataque de diccionario — prueba palabras comunes y contraseñas filtradas conocidas",
      "Fuerza bruta pura — todas las combinaciones posibles de caracteres",
      "Credential Stuffing — usa credenciales de brechas previas en otros servicios",
      "Password Spraying — una contraseña común contra muchos usuarios (evita bloqueos)",
      "Reverse Brute Force — una contraseña específica contra millones de usuarios",
      "Rainbow Tables — tablas precomputadas de hashes para inversión instantánea",
      "Hybrid Attack — combina diccionario con mutaciones (leet speak, sufijos numéricos)",
    ],
    tools: ["Hashcat", "John the Ripper", "Hydra", "Medusa", "Burp Suite Intruder", "Aircrack-ng", "THC-Hydra"],
    indicators: [
      "Múltiples intentos de login fallidos desde la misma IP",
      "Intentos de login en horarios inusuales",
      "Intentos con usernames secuenciales o comunes (admin, root, user)",
      "Bloqueos masivos de cuentas simultáneamente",
      "Picos de tráfico en endpoints de autenticación",
    ],
    prevention: [
      "Autenticación multifactor (MFA/2FA) — hace inútil la contraseña sola",
      "Bloqueo de cuenta después de N intentos fallidos (lockout policy)",
      "CAPTCHA en formularios de login para prevenir automatización",
      "Rate limiting: máximo 5 intentos por minuto por IP",
      "Contraseñas de mínimo 12 caracteres con entropía alta",
      "Detectar y bloquear IPs con comportamiento anómalo",
      "Usar algoritmos de hash lentos: bcrypt, Argon2, scrypt",
      "Passwordless authentication como alternativa (passkeys, FIDO2)",
    ],
    realCase: "En 2020, un ataque de credential stuffing comprometió 500,000 cuentas de Zoom usando contraseñas filtradas de otras brechas. En 2023, Microsoft reportó 4,000 ataques de password spray por segundo contra sus servicios. En 2024, el grupo Scattered Spider comprometió MGM Resorts via social engineering + brute force.",
    impact: "Medio-Alto — directamente proporcional a la debilidad de las contraseñas. El 81% de brechas de datos involucran contraseñas débiles o robadas según el Verizon DBIR.",
    references: [
      { title: "OWASP: Testing for Brute Force (OTG-AUTHN-003)", url: "https://owasp.org/www-project-web-security-testing-guide/v42/4-Web_Application_Security_Testing/04-Authentication_Testing/03-Testing_for_Weak_Lock_Out_Mechanism" },
      { title: "NIST SP 800-63B: Digital Identity Guidelines", url: "https://pages.nist.gov/800-63-3/sp800-63b.html" },
      { title: "HaveIBeenPwned: Credential Stuffing", url: "https://haveibeenpwned.com/FAQs" },
      { title: "MITRE ATT&CK: Brute Force T1110", url: "https://attack.mitre.org/techniques/T1110/" },
    ]
  },
  {
    id: "mitm",
    name: "Man-in-the-Middle",
    fullName: "Man-in-the-Middle Attack (MitM)",
    color: "yellow",
    icon: "🕵️",
    description: "Ataque en el que el adversario se interpone secretamente en la comunicación entre dos partes (usuario-servidor, dos sistemas, etc.) pudiendo interceptar, leer, modificar e inyectar mensajes en tiempo real sin que ninguna de las partes lo detecte. Es especialmente peligroso en redes WiFi públicas y corporativas sin segmentación.",
    how: "El atacante posiciona su dispositivo entre la víctima y el destino legítimo. Primero engaña a ambas partes para que crean estar comunicándose directamente entre sí (ARP spoofing, DNS spoofing). Luego decifra, lee, posiblemente modifica, y reenvía el tráfico. Con SSL stripping puede degradar conexiones HTTPS a HTTP.",
    phases: [
      "Intercepción — el atacante se posiciona en el flujo de comunicación",
      "Descifrado — usa SSL stripping o cert falso para ver tráfico HTTPS",
      "Monitoreo — captura credenciales, tokens de sesión, datos sensibles",
      "Modificación — altera el contenido de los mensajes en tránsito",
      "Inyección — inserta contenido malicioso (scripts, redirects)",
    ],
    types: [
      "ARP Spoofing/Poisoning — envenena la tabla ARP de la red local",
      "DNS Spoofing — responde consultas DNS con IPs maliciosas",
      "SSL Stripping — degrada HTTPS a HTTP eliminando el cifrado",
      "BGP Hijacking — secuestra rutas de internet a nivel de infraestructura",
      "Wi-Fi Eavesdropping — crea AP falso (Evil Twin) que imita red legítima",
      "HTTP Session Hijacking — roba cookies de sesión no cifradas",
      "HTTPS Spoofing — crea dominios similares con certs válidos (homoglyph)",
      "SSL Hijacking — intercepta el handshake TLS con cert propio",
    ],
    tools: ["Ettercap", "Bettercap", "Wireshark", "MITMf", "SSLstrip", "Responder", "mitmproxy", "ARP-SPOOF"],
    indicators: [
      "Cambios inesperados en certificados SSL (warning del navegador)",
      "Latencia inusual en las comunicaciones de red",
      "Entradas ARP duplicadas en la tabla ARP local",
      "Resoluciones DNS inconsistentes para el mismo dominio",
      "Conexiones HTTP donde debería haber HTTPS",
    ],
    prevention: [
      "HTTPS siempre (TLS 1.3) — cifra el canal de extremo a extremo",
      "HSTS (HTTP Strict Transport Security) — fuerza HTTPS permanentemente",
      "Certificate Pinning — verificar que el cert coincide con el esperado",
      "VPN en redes públicas — cifra todo el tráfico saliente",
      "DNSSEC — firma criptográfica de registros DNS",
      "Dynamic ARP Inspection (DAI) en switches corporativos",
      "Verificar siempre el candado y el dominio en el navegador",
      "Autenticación mutua TLS (mTLS) en APIs críticas",
    ],
    realCase: "En 2015, Lenovo preinstalaba Superfish en sus laptops — un adware que instalaba su propio certificado raíz y realizaba MitM en todas las conexiones HTTPS para inyectar anuncios. En 2017, un grupo APT usó BGP hijacking para redirigir tráfico de grandes proveedores ISP a través de Rusia durante 6 meses.",
    impact: "Muy Alto — compromiso total de la confidencialidad e integridad de las comunicaciones. Permite robo de credenciales, tokens de sesión y datos financieros en tiempo real.",
    references: [
      { title: "OWASP: Man-in-the-Middle Attack", url: "https://owasp.org/www-community/attacks/Man-in-the-middle_attack" },
      { title: "Cloudflare: What is a MitM attack?", url: "https://www.cloudflare.com/learning/security/threats/man-in-the-middle-attack/" },
      { title: "NIST: Guidelines for Securing WLANs SP 800-153", url: "https://csrc.nist.gov/publications/detail/sp/800-153/final" },
      { title: "MITRE ATT&CK: Adversary-in-the-Middle T1557", url: "https://attack.mitre.org/techniques/T1557/" },
    ]
  },
  {
    id: "dorking",
    name: "Google Dorking",
    fullName: "Google Hacking / OSINT Dorking",
    color: "cyan",
    icon: "🔍",
    description: "Técnica OSINT (Open Source Intelligence) que usa operadores avanzados de motores de búsqueda para encontrar información sensible expuesta públicamente: credenciales, archivos de configuración, paneles de administración, cámaras IP, bases de datos expuestas y vulnerabilidades en sitios web. No es hacking en sí — solo encuentra lo que ya está público.",
    how: "Se usan operadores especiales (dorks) como site:, filetype:, inurl:, intitle:, intext:, ext: combinados para crear búsquedas quirúrgicas. El motor de búsqueda actúa como el 'hacker' — ya indexó esa información. Google Hacking Database (GHDB) tiene más de 6,000 dorks documentados y clasificados.",
    phases: [
      "Reconocimiento — identificar el objetivo (empresa, dominio, persona)",
      "Selección de dorks — elegir operadores según el objetivo",
      "Búsqueda — ejecutar los dorks en Google, Bing, Shodan, Censys",
      "Análisis — revisar resultados y extraer información útil",
      "Documentación — registrar hallazgos para reporting",
    ],
    types: [
      "filetype:env — archivos .env con credenciales de bases de datos y APIs",
      "filetype:sql site:github.com — dumps de bases de datos en repositorios",
      "inurl:admin intitle:login — paneles de administración expuestos",
      "intext:password filetype:log — logs con contraseñas en texto plano",
      "intitle:index.of — directorios de servidor abiertos sin protección",
      "site:.gov filetype:xls — hojas de cálculo con datos de gobierno",
      "inurl:phpmyadmin — instancias de phpMyAdmin accesibles",
      "Shodan: port:22 country:CO — servidores SSH expuestos en Colombia",
    ],
    tools: ["Google Search con operadores", "Shodan.io", "Censys.io", "GHDB (Exploit-DB)", "DorkSearch", "Gooby", "Pagodo"],
    indicators: [
      "Archivos sensibles indexados por buscadores",
      "Directorios del servidor listados públicamente",
      "Mensajes de error con rutas del sistema expuestas",
      "Versiones de software reveladas en headers HTTP",
      "Credenciales en repositorios públicos de código",
    ],
    prevention: [
      "robots.txt configurado para excluir directorios sensibles",
      "Meta tags noindex en páginas de administración y configuración",
      "Auditorías regulares con Google Search Console",
      "Nunca subir archivos .env, .git, configs a repositorios públicos",
      "Revisar periódicamente qué indexa Google de tu dominio",
      "Configurar .htaccess o nginx para bloquear listado de directorios",
      "Usar herramientas como GitLeaks para detectar secretos en código",
      "Monitoreo de exposición con servicios como Have I Been Pwned o SpyCloud",
    ],
    realCase: "En 2022, usando Google Dorking se encontraron más de 10 millones de credenciales expuestas en archivos .env en sitios de producción. En 2023, un investigador encontró conexiones directas a bases de datos MySQL de hospitales usando dorks de Shodan. El grupo Lapsus$ usó Dorking para encontrar credenciales de Nvidia, Samsung y Microsoft antes de robar sus datos.",
    impact: "Variable — desde exposición de información hasta compromiso total del sistema. Frecuentemente el primer paso en ataques más complejos (reconocimiento).",
    references: [
      { title: "Exploit-DB: Google Hacking Database (GHDB)", url: "https://www.exploit-db.com/google-hacking-database" },
      { title: "OWASP: Google Hacking", url: "https://owasp.org/www-community/attacks/Google_Hacking" },
      { title: "Shodan: The Search Engine for IoT", url: "https://www.shodan.io" },
      { title: "MITRE ATT&CK: Search Open Websites T1593", url: "https://attack.mitre.org/techniques/T1593/" },
    ]
  },
  {
    id: "social",
    name: "Ingeniería Social",
    fullName: "Social Engineering",
    color: "purple",
    icon: "🎭",
    description: "Conjunto de técnicas psicológicas de manipulación para engañar a personas y lograr que divulguen información confidencial, realicen acciones no autorizadas o concedan acceso a sistemas. Explota sesgos cognitivos humanos: confianza, autoridad, urgencia, reciprocidad y miedo. Es el vector de ataque más efectivo porque el humano es el eslabón más débil.",
    how: "El atacante investiga previamente a la víctima (LinkedIn, redes sociales, OSINT) para construir un pretexto creíble. Se hace pasar por alguien de confianza (IT support, jefe, banco, proveedor) y usa principios psicológicos de influencia para obtener lo que necesita sin despertar sospechas.",
    phases: [
      "Research — recopilar información de la víctima via OSINT",
      "Pretexting — construir una historia y personaje creíbles",
      "Approach — contactar a la víctima por el canal elegido",
      "Rapport — generar confianza y reducir defensas",
      "Exploitation — solicitar la información o acción objetivo",
      "Exit — retirarse sin levantar sospechas",
    ],
    types: [
      "Phishing — correos fraudulentos con links o adjuntos maliciosos",
      "Vishing — llamadas telefónicas haciéndose pasar por soporte técnico o banco",
      "Smishing — SMS fraudulentos con links maliciosos",
      "Pretexting — crear escenario falso para justificar la solicitud",
      "Baiting — dejar USBs infectados en estacionamientos o lobbies",
      "Quid Pro Quo — ofrecer algo a cambio de información",
      "Tailgating — seguir a alguien para acceder físicamente sin credencial",
      "Watering Hole — comprometer sitios web que frecuenta el objetivo",
    ],
    tools: ["SET (Social Engineering Toolkit)", "Gophish", "King Phisher", "Modlishka", "BeEF", "ZPhisher"],
    indicators: [
      "Solicitudes urgentes inusuales de personas conocidas",
      "Pedidos de información sensible por canales no habituales",
      "Presión para actuar rápido sin verificar",
      "Historias demasiado elaboradas o emotivas",
      "Solicitudes que omiten procesos normales de aprobación",
    ],
    prevention: [
      "Capacitación y concientización continua del personal (security awareness)",
      "Simulacros de phishing periódicos para medir vulnerabilidad",
      "Verificación de identidad en múltiples pasos antes de dar información",
      "Política clara: nunca compartir contraseñas por ningún medio",
      "Procedimientos formales para solicitudes urgentes o inusuales",
      "Cultura de seguridad: está bien cuestionar solicitudes sospechosas",
      "Línea directa de reporte de intentos de ingeniería social",
    ],
    realCase: "En julio 2020, atacantes llamaron por teléfono a empleados de Twitter haciéndose pasar por soporte de IT y obtuvieron acceso a herramientas internas. Comprometieron cuentas de Elon Musk, Barack Obama, Apple y Joe Biden para un fraude de Bitcoin de $120,000. En 2023, el grupo Scattered Spider usó vishing contra empleados de MGM Resorts para acceder a su AD.",
    impact: "Muy Alto — el 98% de los ciberataques incluyen algún componente de ingeniería social según IBM. Costo promedio de una brecha iniciada por ingeniería social: $4.76 millones USD.",
    references: [
      { title: "SANS: Social Engineering Fundamentals", url: "https://www.sans.org/white-papers/1232/" },
      { title: "CISA: Avoiding Social Engineering Attacks", url: "https://www.cisa.gov/news-events/news/avoiding-social-engineering-and-phishing-attacks" },
      { title: "MITRE ATT&CK: Phishing for Information T1598", url: "https://attack.mitre.org/techniques/T1598/" },
      { title: "Verizon DBIR 2024: Human Element", url: "https://www.verizon.com/business/resources/reports/dbir/" },
    ]
  },
  {
    id: "phishing",
    name: "Phishing",
    fullName: "Phishing Attack",
    color: "pink",
    icon: "🎣",
    description: "Ataque de ingeniería social que usa comunicaciones fraudulentas (emails, SMS, sitios web falsos) que imitan fuentes legítimas para robar credenciales, datos bancarios o instalar malware. Es el ciberataque más prevalente del mundo — el 36% de todas las brechas de datos en 2023 comenzaron con un email de phishing según Verizon DBIR.",
    how: "El atacante registra un dominio similar al objetivo (paypa1.com vs paypal.com), clona el sitio web original pixel por pixel, y envía emails masivos con un pretexto convincente (cuenta suspendida, paquete retenido, factura pendiente). La víctima ingresa sus credenciales en el sitio falso sin notar la diferencia.",
    phases: [
      "Preparación — registrar dominio similar, clonar sitio, preparar email",
      "Lanzamiento — envío masivo de emails con técnicas de evasión de spam",
      "Captura — la víctima hace clic y llega al sitio falso",
      "Robo — la víctima ingresa sus credenciales (capturadas en tiempo real)",
      "Uso — el atacante usa las credenciales inmediatamente o las vende",
    ],
    types: [
      "Email Phishing — emails masivos con links a sitios falsos",
      "Spear Phishing — dirigido a persona específica con información personalizada",
      "Whaling — dirigido a ejecutivos de alto nivel (CEO fraud)",
      "Clone Phishing — copia exacta de email legítimo con link malicioso",
      "Smishing — phishing por SMS (texto)",
      "Vishing — phishing por llamada de voz",
      "Angler Phishing — a través de redes sociales y mensajes directos",
      "Pharming — envenenamiento DNS para redirigir a sitio falso automáticamente",
    ],
    tools: ["Gophish", "King Phisher", "Evilginx2 (proxy inverso AiTM)", "Zphisher", "Modlishka", "PhishX"],
    indicators: [
      "Dominio del remitente ligeramente diferente al original",
      "URL con caracteres especiales o subdominios sospechosos",
      "Sensación de urgencia: cuenta bloqueada, oferta expira, acción requerida",
      "Saludos genéricos: Estimado cliente, Dear User",
      "Links que no coinciden con el texto mostrado al pasar el cursor",
      "Archivos adjuntos no solicitados (.doc, .xls, .pdf con macros)",
    ],
    prevention: [
      "Verificar siempre el dominio exacto del remitente y del sitio",
      "No hacer clic en links — ir directamente al sitio escribiendo la URL",
      "Usar un gestor de contraseñas (detecta dominios falsos automáticamente)",
      "2FA en todas las cuentas — invalida las credenciales robadas",
      "Filtros anti-phishing en el servidor de correo (DMARC, SPF, DKIM)",
      "Capacitación con simulacros de phishing periódicos",
      "Reportar emails sospechosos al equipo de seguridad",
      "Extensiones de browser como Netcraft o Google Safe Browsing",
    ],
    realCase: "En 2016, un email de spear phishing al director de campaña de Hillary Clinton (John Podesta) resultó en la filtración de 50,000 correos que alteraron el curso de las elecciones. En 2023, MGM Resorts y Caesars fueron víctimas de phishing + social engineering con pérdidas superiores a $100 millones. En 2024, el ataque de phishing a Change Healthcare afectó a 1 de cada 3 estadounidenses.",
    impact: "Crítico — vector inicial del 36% de todas las brechas. Daño promedio: $4.91 millones USD por incidente según IBM Cost of Data Breach 2024.",
    references: [
      { title: "APWG: Phishing Activity Trends Report 2024", url: "https://apwg.org/resources/apwg-reports/" },
      { title: "Google: Safe Browsing Transparency Report", url: "https://transparencyreport.google.com/safe-browsing/overview" },
      { title: "NIST: Phishing Guidance SP 1800-17", url: "https://www.nist.gov/system/files/documents/2019/08/28/nist_phishing_guidance.pdf" },
      { title: "MITRE ATT&CK: Phishing T1566", url: "https://attack.mitre.org/techniques/T1566/" },
    ]
  },
  {
    id: "ransomware",
    name: "Ransomware",
    fullName: "Ransomware Attack",
    color: "red",
    icon: "🔐",
    description: "Tipo de malware que cifra todos los archivos accesibles del sistema víctima (locales, en red, en backups conectados) y exige un rescate en criptomonedas para restaurar el acceso. Es la amenaza de ciberseguridad más costosa: en 2023 los pagos globales de rescate superaron los $1.1 billones de dólares según Chainalysis.",
    how: "El ransomware ingresa principalmente via phishing o RDP expuesto. Una vez dentro, ejecuta reconocimiento de la red, escala privilegios, se mueve lateralmente a otros sistemas, exfiltra datos (para doble extorsión) y finalmente cifra todo usando AES-256 + RSA-2048. La clave de descifrado solo existe en el servidor del atacante.",
    phases: [
      "Acceso inicial — phishing, RDP expuesto, VPN sin parchar, supply chain",
      "Persistencia — instala backdoors, modifica registro, deshabilita antivirus",
      "Reconocimiento interno — mapea la red, identifica backups y servidores críticos",
      "Movimiento lateral — se propaga a otros sistemas via SMB, WMI, PsExec",
      "Exfiltración — roba datos antes de cifrar (para doble extorsión)",
      "Cifrado — AES-256 para archivos, RSA-2048 para la clave AES",
      "Extorsión — nota de rescate, sitio .onion, amenaza de publicación",
    ],
    types: [
      "Crypto Ransomware — cifra archivos con algoritmos fuertes (WannaCry, REvil)",
      "Locker Ransomware — bloquea el sistema completo sin cifrar archivos",
      "Double Extortion — cifra Y amenaza publicar los datos robados",
      "Triple Extortion — añade DDoS o contacto a clientes/medios como presión",
      "RaaS (Ransomware as a Service) — plataforma para afiliados sin conocimiento técnico",
      "Wiper — se disfraza de ransomware pero destruye datos permanentemente",
    ],
    tools: ["WannaCry", "REvil/Sodinokibi", "LockBit 3.0", "BlackCat/ALPHV", "Conti", "Cl0p", "Black Basta"],
    indicators: [
      "Archivos con extensiones desconocidas (.locked, .encrypted, .ryuk)",
      "Nota de rescate (README.txt, HOW_TO_DECRYPT.html) en cada carpeta",
      "Procesos desconocidos consumiendo CPU/disco masivamente",
      "Tráfico de red hacia IPs desconocidas en puertos altos",
      "Desactivación del antivirus y shadow copies (vssadmin delete)",
      "Cuentas de administrador creadas sin autorización",
    ],
    prevention: [
      "Backups offline regulares — regla 3-2-1 (3 copias, 2 medios, 1 offsite)",
      "Parches de seguridad al día — especialmente SMB, RDP y VPN",
      "No exponer RDP directamente a internet — usar VPN con MFA",
      "EDR/XDR en endpoints — detección de comportamiento anómalo",
      "Segmentación de red — limitar el movimiento lateral",
      "Privilegio mínimo — ningún usuario con acceso innecesario",
      "Plan de respuesta a incidentes documentado y probado",
      "Deshabilitar macros en Office por defecto",
    ],
    realCase: "En mayo 2021, el ransomware DarkSide atacó Colonial Pipeline paralizando el 45% del suministro de combustible de la costa este de EE.UU. La empresa pagó $4.4 millones en Bitcoin; el FBI recuperó $2.3 millones. En 2024, el ataque de Cl0p a MOVEit afectó a más de 2,700 organizaciones incluyendo agencias del gobierno de EE.UU.",
    impact: "Crítico — costo promedio de un ataque de ransomware en 2024: $5.13 millones USD (sin contar el rescate). El 66% de organizaciones afectadas tardaron más de 1 mes en recuperarse.",
    references: [
      { title: "CISA: #StopRansomware Guide 2024", url: "https://www.cisa.gov/stopransomware/ransomware-guide" },
      { title: "FBI: IC3 Ransomware Report 2023", url: "https://www.ic3.gov/Media/PDF/AnnualReport/2023_IC3Report.pdf" },
      { title: "Chainalysis: Crypto Crime Report 2024", url: "https://www.chainalysis.com/blog/crypto-crime-report-2024/" },
      { title: "MITRE ATT&CK: Data Encrypted for Impact T1486", url: "https://attack.mitre.org/techniques/T1486/" },
    ]
  },
  {
    id: "sqli",
    name: "SQL Injection",
    fullName: "SQL Injection (SQLi)",
    color: "green",
    icon: "💉",
    description: "Vulnerabilidad de seguridad web que permite insertar código SQL malicioso en consultas de base de datos a través de inputs de usuario no validados. Es la vulnerabilidad #3 del OWASP Top 10 2021 y existe desde 1998. Puede resultar en acceso, modificación o eliminación total de la base de datos, bypass de autenticación y ejecución de comandos en el servidor.",
    how: "La aplicación construye consultas SQL concatenando directamente el input del usuario: SELECT * FROM users WHERE user='$input'. El atacante ingresa: ' OR '1'='1 y la consulta retorna todos los usuarios. Con UNION SELECT puede extraer datos de otras tablas. Con xp_cmdshell en SQL Server puede ejecutar comandos del SO.",
    phases: [
      "Detección — identificar parámetros vulnerables (', --, ;, OR 1=1)",
      "Fingerprinting — determinar el tipo de BD (MySQL, MSSQL, Oracle, PostgreSQL)",
      "Extracción — obtener nombre de BD, tablas, columnas",
      "Dumping — extraer datos sensibles (usuarios, contraseñas, tarjetas)",
      "Escalada — intentar escritura en disco o ejecución de comandos (OS command)",
    ],
    types: [
      "In-band SQLi (Error-based) — extrae datos a través de mensajes de error",
      "In-band SQLi (Union-based) — usa UNION SELECT para combinar resultados",
      "Blind SQLi (Boolean-based) — hace preguntas SI/NO por comportamiento",
      "Blind SQLi (Time-based) — usa SLEEP() para inferir datos por tiempo de respuesta",
      "Out-of-band SQLi — exfiltra datos via DNS o HTTP a servidor externo",
      "Second-order SQLi — payload se almacena y ejecuta en segunda petición",
      "Stored SQLi — payload persistente en la BD afecta a múltiples usuarios",
    ],
    tools: ["SQLMap (automatización completa)", "Havij", "jSQL Injection", "Burp Suite Scanner", "BBQSQL", "NoSQLMap"],
    indicators: [
      "Mensajes de error de SQL expuestos en la aplicación",
      "Comportamiento diferente al agregar ' o -- en parámetros",
      "Respuestas más lentas al agregar SLEEP() o WAITFOR DELAY",
      "Logs con queries malformadas o con palabras clave SQL",
      "Acceso a datos de otros usuarios o tablas no esperadas",
    ],
    prevention: [
      "Prepared Statements / Consultas parametrizadas — solución definitiva",
      "ORM frameworks (Hibernate, Eloquent, SQLAlchemy) — abstrae el SQL",
      "Validación y sanitización estricta de todos los inputs",
      "WAF (Web Application Firewall) — detecta y bloquea payloads SQLi",
      "Principio de mínimo privilegio en la BD — el usuario de app no es DBA",
      "Deshabilitar mensajes de error detallados en producción",
      "Auditorías de código y pentesting periódicos",
      "Usar Stored Procedures — si están bien implementados",
    ],
    realCase: "En 2008, el grupo RBN usó SQL Injection para robar 130 millones de números de tarjeta de Heartland Payment Systems — el mayor robo financiero hasta entonces. En 2023, MOVEit Transfer tenía una vulnerabilidad SQLi (CVE-2023-34362) que el grupo Cl0p explotó para robar datos de más de 2,700 organizaciones. En 2024, SQLMap sigue siendo la herramienta #1 en CTFs y pentests.",
    impact: "Crítico — puede resultar en robo completo de la base de datos, bypass de autenticación y control del servidor. OWASP clasifica Injection como A03:2021.",
    references: [
      { title: "OWASP: SQL Injection Prevention Cheat Sheet", url: "https://cheatsheetseries.owasp.org/cheatsheets/SQL_Injection_Prevention_Cheat_Sheet.html" },
      { title: "PortSwigger: SQL Injection Tutorial", url: "https://portswigger.net/web-security/sql-injection" },
      { title: "MITRE CWE-89: Improper Neutralization of SQL", url: "https://cwe.mitre.org/data/definitions/89.html" },
      { title: "MITRE ATT&CK: Exploit Public-Facing Application T1190", url: "https://attack.mitre.org/techniques/T1190/" },
    ]
  },
];

const COLOR_MAP = {
  red:    { border:"border-red-500/40",    text:"text-red-400",    bg:"bg-red-500/10",    badge:"bg-red-500/20 text-red-300"    },
  orange: { border:"border-orange-500/40", text:"text-orange-400", bg:"bg-orange-500/10", badge:"bg-orange-500/20 text-orange-300" },
  yellow: { border:"border-yellow-500/40", text:"text-yellow-400", bg:"bg-yellow-500/10", badge:"bg-yellow-500/20 text-yellow-300" },
  cyan:   { border:"border-cyan-500/40",   text:"text-cyan-400",   bg:"bg-cyan-500/10",   badge:"bg-cyan-500/20 text-cyan-300"   },
  purple: { border:"border-purple-500/40", text:"text-purple-400", bg:"bg-purple-500/10", badge:"bg-purple-500/20 text-purple-300" },
  pink:   { border:"border-pink-500/40",   text:"text-pink-400",   bg:"bg-pink-500/10",   badge:"bg-pink-500/20 text-pink-300"   },
  green:  { border:"border-green-500/40",  text:"text-green-400",  bg:"bg-green-500/10",  badge:"bg-green-500/20 text-green-300"  },
};

// ── PANEL INFORMATIVO DE ATAQUES ──────────────────────────────
const InfoPanel = ({ onClose }) => {
  const [selected, setSelected] = React.useState(null);
  const attack = selected ? ATTACK_DATA.find(a => a.id === selected) : null;
  const c = attack ? (COLOR_MAP[attack.color] || COLOR_MAP.cyan) : null;

  return (
    <div className="fixed inset-0 z-50 flex bg-black/80 backdrop-blur-sm">
      <style>{GLOBAL_STYLES}</style>

      {/* PANEL LATERAL IZQUIERDO — MENÚ */}
      <div className="w-72 shrink-0 bg-gray-950 border-r border-cyan-500/20 flex flex-col">
        <div className="px-6 py-5 border-b border-gray-800 flex items-center justify-between">
          <div>
            <h2 className="font-orbitron text-sm font-black text-white uppercase tracking-widest">Tipos de Ataques</h2>
            <p className="text-cyan-500/50 text-[10px] font-mono-cyber uppercase mt-1">Solo informativo</p>
          </div>
          <button onClick={onClose} className="text-gray-600 hover:text-white transition-colors cursor-pointer">
            <X size={18}/>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto py-3">
          {ATTACK_DATA.map(a => {
            const ac = COLOR_MAP[a.color] || COLOR_MAP.cyan;
            return (
              <button key={a.id} onClick={() => setSelected(a.id)}
                className={`w-full text-left px-5 py-4 flex items-center gap-3 transition-all cursor-pointer border-l-2 ${
                  selected === a.id
                    ? `${ac.bg} ${ac.border} border-l-2`
                    : 'border-transparent hover:bg-white/5 border-transparent'
                }`}>
                <span className="text-xl">{a.icon}</span>
                <div>
                  <p className={`font-orbitron text-xs font-black uppercase ${selected === a.id ? ac.text : 'text-gray-300'}`}>{a.name}</p>
                  <p className="text-gray-600 text-[10px] font-mono-cyber uppercase truncate max-w-[160px]">{a.fullName}</p>
                </div>
              </button>
            );
          })}
        </div>
        <div className="px-5 py-4 border-t border-gray-800">
          <p className="text-gray-700 text-[9px] font-mono-cyber uppercase text-center">Fuentes: OWASP · NIST · CISA · MITRE</p>
        </div>
      </div>

      {/* PANEL DERECHO — CONTENIDO */}
      <div className="flex-1 bg-gray-950 overflow-y-auto">
        {!attack ? (
          <div className="flex flex-col items-center justify-center h-full">
            <div className="text-6xl mb-6">🛡️</div>
            <h3 className="font-orbitron text-xl font-black text-white uppercase tracking-widest mb-3">Enciclopedia de Ataques</h3>
            <p className="text-gray-600 font-mono-cyber text-sm uppercase text-center max-w-sm">Selecciona un tipo de ataque del menú para ver información detallada, herramientas, prevención y casos reales.</p>
          </div>
        ) : (
          <div className="p-8 max-w-3xl">
            {/* HEADER */}
            <div className={`${c.bg} border ${c.border} p-6 mb-6`}
              style={{clipPath:'polygon(0 0,calc(100% - 16px) 0,100% 16px,100% 100%,0 100%)'}}>
              <div className="flex items-center gap-4 mb-3">
                <span className="text-4xl">{attack.icon}</span>
                <div>
                  <h2 className={`font-orbitron text-2xl font-black uppercase tracking-widest ${c.text}`}>{attack.name}</h2>
                  <p className="text-gray-400 font-mono-cyber text-xs uppercase">{attack.fullName}</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm font-rajdhani leading-relaxed">{attack.description}</p>
            </div>

            {/* CÓMO FUNCIONA */}
            <div className="bg-gray-900/60 border border-gray-800 p-6 mb-4"
              style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
              <h3 className="font-mono-cyber text-[10px] text-cyan-500/70 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span>⚙️</span> Cómo Funciona
              </h3>
              <p className="text-gray-300 text-sm font-rajdhani leading-relaxed">{attack.how}</p>
            </div>

            {/* FASES DEL ATAQUE */}
            {attack.phases && (
              <div className="bg-gray-900/60 border border-gray-800 p-6 mb-4"
                style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
                <h3 className="font-mono-cyber text-[10px] text-cyan-500/70 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <span>📋</span> Fases del Ataque (Kill Chain)
                </h3>
                <div className="space-y-2">
                  {attack.phases.map((p,i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span className="text-cyan-500 font-orbitron text-xs font-black shrink-0 mt-0.5">{String(i+1).padStart(2,'0')}</span>
                      <span className="text-gray-300 text-sm font-rajdhani leading-relaxed">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* VARIANTES */}
            <div className="bg-gray-900/60 border border-gray-800 p-6 mb-4"
              style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
              <h3 className="font-mono-cyber text-[10px] text-cyan-500/70 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span>🔀</span> Variantes / Tipos
              </h3>
              <div className="space-y-1.5">
                {attack.types.map((t,i) => (
                  <div key={i} className={`px-3 py-2 text-[11px] font-rajdhani ${c.bg} border ${c.border}`}>
                    <span className={`font-bold ${c.text} mr-1`}>▸</span>{t}
                  </div>
                ))}
              </div>
            </div>

            {/* INDICADORES DE COMPROMISO */}
            {attack.indicators && (
              <div className="bg-orange-900/10 border border-orange-500/20 p-6 mb-4"
                style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
                <h3 className="font-mono-cyber text-[10px] text-orange-500/70 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <span>🚨</span> Indicadores de Compromiso (IoC)
                </h3>
                <ul className="space-y-2">
                  {attack.indicators.map((ind,i) => (
                    <li key={i} className="flex items-start gap-2 text-sm font-rajdhani text-orange-200/80">
                      <span className="text-orange-400 mt-0.5 shrink-0">⚠</span>{ind}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* HERRAMIENTAS CONOCIDAS */}
            <div className="bg-gray-900/60 border border-gray-800 p-6 mb-4"
              style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
              <h3 className="font-mono-cyber text-[10px] text-red-500/70 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span>🛠️</span> Herramientas Conocidas
              </h3>
              <div className="flex flex-wrap gap-2">
                {attack.tools.map((t,i) => (
                  <span key={i} className="px-3 py-1.5 text-[11px] font-mono-cyber uppercase bg-red-500/10 text-red-400 border border-red-500/20">{t}</span>
                ))}
              </div>
            </div>

            {/* PREVENCIÓN */}
            <div className="bg-gray-900/60 border border-gray-800 p-6 mb-4"
              style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
              <h3 className="font-mono-cyber text-[10px] text-green-500/70 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span>🛡️</span> Medidas de Prevención
              </h3>
              <ul className="space-y-2">
                {attack.prevention.map((p,i) => (
                  <li key={i} className="flex items-start gap-2 text-sm font-rajdhani text-gray-300">
                    <span className="text-green-400 mt-0.5 shrink-0">✓</span>{p}
                  </li>
                ))}
              </ul>
            </div>

            {/* IMPACTO */}
            {attack.impact && (
              <div className="bg-red-900/10 border border-red-500/20 p-6 mb-4"
                style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
                <h3 className="font-mono-cyber text-[10px] text-red-500/70 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <span>💀</span> Nivel de Impacto
                </h3>
                <p className="text-red-200/80 text-sm font-rajdhani leading-relaxed">{attack.impact}</p>
              </div>
            )}

            {/* CASO REAL */}
            <div className="bg-amber-900/10 border border-amber-500/20 p-6 mb-4"
              style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
              <h3 className="font-mono-cyber text-[10px] text-amber-500/70 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span>📰</span> Casos Reales Documentados
              </h3>
              <p className="text-amber-200/80 text-sm font-rajdhani leading-relaxed">{attack.realCase}</p>
            </div>

            {/* REFERENCIAS */}
            <div className="bg-gray-900/60 border border-gray-800 p-6"
              style={{clipPath:'polygon(0 0,calc(100% - 12px) 0,100% 12px,100% 100%,0 100%)'}}>
              <h3 className="font-mono-cyber text-[10px] text-blue-500/70 uppercase tracking-widest mb-3 flex items-center gap-2">
                <span>📚</span> Referencias y Fuentes Verificadas
              </h3>
              <div className="space-y-2">
                {attack.references.map((r,i) => (
                  <a key={i} href={r.url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300 text-xs font-mono-cyber uppercase transition-colors group">
                    <Globe size={11} className="shrink-0"/>
                    <span className="group-hover:underline">{r.title}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function App() {
  const [geoStatus,       setGeoStatus]       = useState('checking');
  const [user,            setUser]            = useState(null);
  const [view,            setView]            = useState('login');
  const [loading,         setLoading]         = useState(false);
  const [errorMsg,        setErrorMsg]        = useState('');
  const [successMsg,      setSuccessMsg]      = useState('');
  const [requires2FA,     setRequires2FA]     = useState(false);
  const [tempEmail,       setTempEmail]       = useState("");
  const [verifyTokenSent, setVerifyTokenSent] = useState(null);
  const [regData,         setRegData]         = useState(null);
  const [captcha,         setCaptcha]         = useState({n1:0,n2:0});
  const [captchaIn,       setCaptchaIn]       = useState('');
  const [termsAccepted,   setTermsAccepted]   = useState(false);
  const [showTermsModal,  setShowTermsModal]  = useState(false);
  const [setupSecret,     setSetupSecret]     = useState(null);
  const [currentIp,       setCurrentIp]       = useState("Calculando...");
  const [showActividades,  setShowActividades]  = useState(false);
  const [actividadesToken, setActividadesToken] = useState(null);
  const [tokenLoading,     setTokenLoading]     = useState(false);
  // Estados herramientas
  const [showHerramienta,  setShowHerramienta]  = useState(null);
  const [showInfo,         setShowInfo]         = useState(false);
  const [showMetadatos,    setShowMetadatos]    = useState(false); // 'url'|'password'|'email'|'osint'
  const [htLoading,        setHtLoading]        = useState(false);
  const [htResult,         setHtResult]         = useState(null);
  const [htError,          setHtError]          = useState('');
  const [htInput,          setHtInput]          = useState('');

  useEffect(()=>{
    const hc=(e)=>e.preventDefault();
    document.addEventListener('contextmenu',hc);
    checkLocation().then(ok=>setGeoStatus(ok?'allowed':'blocked'));
    if(view==='register'){generateCaptcha();setTermsAccepted(false);}
    getClientMetadata().then(m=>setCurrentIp(m.ip));
    return ()=>document.removeEventListener('contextmenu',hc);
  },[view]);

  useSessionTimeout(!!user,()=>{
    setUser(null);setView('login');setActividadesToken(null);setShowActividades(false);
    setErrorMsg("Sesión expirada por inactividad.");
  });

  const generateCaptcha=()=>{setCaptcha({n1:Math.floor(Math.random()*9)+1,n2:Math.floor(Math.random()*9)+1});setCaptchaIn('');};

  const reportLog=async(email,status)=>{
    try{
      const meta=await getClientMetadata();
      const p=new URLSearchParams();
      p.append('action','logAccess');p.append('email',sanitizeInput(email));
      p.append('ip',meta.ip);p.append('device',meta.userAgent);
      p.append('status',status);p.append('lat',meta.lat);p.append('lng',meta.lng);p.append('clientIP',meta.ip);
      await fetch(APPS_SCRIPT_URL,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},body:p.toString()});
    }catch{}
  };

  const handleLogin=async(e)=>{
    e.preventDefault();setLoading(true);setErrorMsg('');
    const email=e.target.email.value.toLowerCase().trim();
    const password=e.target.password.value.trim();
    try{
      const res=await fetch(APPS_SCRIPT_URL,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},
        body:new URLSearchParams({action:'login',email,password,clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"}).toString()});
      const data=JSON.parse(await res.text());
      if(data.result==='require_2fa'){setTempEmail(email);setRequires2FA(true);}
      else{await reportLog(email,"LOGIN_FAIL_CREDENTIALS");setErrorMsg(data.message||'Credenciales inválidas.');}
    }catch{setErrorMsg('Error de conexión.');}finally{setLoading(false);}
  };

  const startRegistration=async(e)=>{
    e.preventDefault();setErrorMsg('');
    if(!termsAccepted){setErrorMsg("Acepta las políticas Ley 1581.");return;}
    if(parseInt(captchaIn)!==(captcha.n1+captcha.n2)){setErrorMsg("Captcha incorrecto.");generateCaptcha();return;}
    const fd=new FormData(e.target);const data=Object.fromEntries(fd);
    const vd=['gmail.com','outlook.com','hotmail.com','yahoo.com','icloud.com','live.com'];
    const ed=data.email.split('@')[1];
    if(!ed||!vd.includes(ed)){setErrorMsg("Dominio de correo inválido.");return;}
    if(data.password!==data.confirmPassword){setErrorMsg("Contraseñas no coinciden.");return;}
    if(data.name)data.name=data.name.toUpperCase().replace(/[^A-ZÑÁÉÍÓÚ\s]/g,'');
    delete data.confirmPassword;
    setLoading(true);
    try{
      const p=new URLSearchParams({action:'sendVerifyCode',email:data.email,clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"});
      const res=await fetch(APPS_SCRIPT_URL,{method:'POST',body:p});
      const json=JSON.parse(await res.text());
      if(json.result==='success'){setVerifyTokenSent(true);setRegData(data);setSuccessMsg("Código enviado.");}
      else setErrorMsg(String(json.message));
    }catch{setErrorMsg("Error de red.");}finally{setLoading(false);}
  };

  const completeRegistration=async(e)=>{
    e.preventDefault();setErrorMsg('');
    const code=String(e.target.vcode.value).trim();setLoading(true);
    try{
      const vp=new URLSearchParams({action:'verifyRegCode',email:regData.email,code,clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"});
      const vr=await fetch(APPS_SCRIPT_URL,{method:'POST',body:vp});
      const vj=JSON.parse(await vr.text());
      if(vj.result!=='success'){setErrorMsg(vj.message||"Código incorrecto.");setLoading(false);return;}
      const p=new URLSearchParams(regData);p.append('action','register');p.append('clientIP',currentIp!=="Calculando..."?currentIp:"UNKNOWN");
      const res=await fetch(APPS_SCRIPT_URL,{method:'POST',body:p});
      const json=JSON.parse(await res.text());
      if(json.result==='success'){setSetupSecret(json.secret);setVerifyTokenSent(null);await reportLog(regData.email,"2FA_ACTIVATION_SUCCESS");}
      else setErrorMsg("Error al registrar.");
    }catch{setErrorMsg("Error de conexión.");}finally{setLoading(false);}
  };

  const handleForgot=async(e)=>{
    e.preventDefault();setLoading(true);setErrorMsg('');
    try{
      const p=new URLSearchParams({action:'forgotPassword',email:sanitizeInput(e.target.email.value.trim()),clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"});
      const res=await fetch(APPS_SCRIPT_URL,{method:'POST',body:p});
      const data=JSON.parse(await res.text());
      if(data.result==='success'){setSuccessMsg("Token enviado.");setView('reset');generateCaptcha();}
      else setErrorMsg(String(data.message));
    }catch{setErrorMsg("Error de red.");}finally{setLoading(false);}
  };

  const handleUpdatePass=async(e)=>{
    e.preventDefault();setErrorMsg('');
    if(parseInt(captchaIn)!==(captcha.n1+captcha.n2)){setErrorMsg("Captcha incorrecto.");generateCaptcha();return;}
    setLoading(true);
    const fd=new FormData(e.target);const data=Object.fromEntries(fd);
    const p=new URLSearchParams({action:'updatePassword',...data,clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"});
    try{
      const res=await fetch(APPS_SCRIPT_URL,{method:'POST',body:p});
      const json=JSON.parse(await res.text());
      if(json.result==='success'){setSuccessMsg("Clave actualizada.");await reportLog(data.email,"PASSWORD_CHANGE_SUCCESS");setTimeout(()=>setView('login'),2000);}
      else{setErrorMsg(String(json.message));await reportLog(data.email,"PASSWORD_CHANGE_FAIL");}
    }catch{setErrorMsg("Error en servidor.");}finally{setLoading(false);}
  };

  const accederDorking=()=>{ window.location.href=DORKING_URL; };

  // ── HERRAMIENTAS ─────────────────────────────────────────────
  const abrirHerramienta=(tool)=>{ setShowHerramienta(tool); setHtResult(null); setHtError(''); setHtInput(''); };
  const cerrarHerramienta=()=>{ setShowHerramienta(null); setHtResult(null); setHtError(''); setHtInput(''); };

  const runHerramienta=async(action, bodyParams)=>{
    setHtLoading(true); setHtError(''); setHtResult(null);
    try {
      const res  = await fetch(HERRAMIENTAS_URL, {
        method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'},
        body: new URLSearchParams({...bodyParams, email:user.email, clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"}).toString()
      });
      const data = JSON.parse(await res.text());
      if(data.result==='success') setHtResult(data);
      else setHtError(data.message||'Error al procesar.');
    } catch { setHtError('Error de conexión con el servidor.'); } finally { setHtLoading(false); }
  };

  // SHA-1 para k-anonymity de contraseñas (se calcula en el navegador)
  const sha1 = async(str)=>{
    const buf  = await crypto.subtle.digest('SHA-1', new TextEncoder().encode(str));
    return Array.from(new Uint8Array(buf)).map(b=>b.toString(16).padStart(2,'0')).join('').toUpperCase();
  };

  const checkPassword=async(pwd)=>{
    if(!pwd){setHtError('Ingresa una contraseña.');return;}
    setHtLoading(true); setHtError(''); setHtResult(null);
    try {
      const hash   = await sha1(pwd);
      const prefix = hash.substring(0,5);
      const suffix = hash.substring(5);
      const res    = await fetch(HERRAMIENTAS_URL, {
        method:'POST', headers:{'Content-Type':'application/x-www-form-urlencoded'},
        body: new URLSearchParams({action:'checkPassword', hashPrefix:prefix, email:user.email, clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"}).toString()
      });
      const data   = JSON.parse(await res.text());
      if(data.result==='success'){
        const match = data.suffixes.find(s=>s.suffix.toUpperCase()===suffix);
        setHtResult({ type:'password', compromised: !!match, count: match?match.count:0 });
      } else setHtError(data.message||'Error.');
    } catch { setHtError('Error de conexión.'); } finally { setHtLoading(false); }
  };

  const accederActividades=async()=>{
    if(actividadesToken){setShowActividades(true);return;}
    setTokenLoading(true);
    try{
      const res=await fetch(ACTIVIDADES_URL,{method:'POST',headers:{'Content-Type':'application/x-www-form-urlencoded'},
        body:new URLSearchParams({action:'getToken',email:user.email,clientIP:currentIp!=="Calculando..."?currentIp:"UNKNOWN"}).toString()});
      const data=JSON.parse(await res.text());
      if(data.result==='success'){setActividadesToken(data.token);setShowActividades(true);}
      else alert("Error: "+(data.message||"Sin sesión."));
    }catch{alert("Error de conexión.");}finally{setTokenLoading(false);}
  };

  // ── RENDERS ───────────────────────────────────────────────
  if(geoStatus==='checking') return (
    <div className="min-h-screen bg-gray-950 cyber-grid flex items-center justify-center">
      <style>{GLOBAL_STYLES}</style>
      <p className="font-mono-cyber text-cyan-400 animate-pulse uppercase tracking-widest">Verificando región...</p>
    </div>
  );

  if(geoStatus==='insecure') return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center text-center p-12">
      <style>{GLOBAL_STYLES}</style>
      <ShieldAlert size={64} className="mb-6 text-red-500 animate-bounce"/>
      <h1 className="font-orbitron text-3xl text-red-500 mb-4 uppercase">Conexión No Segura</h1>
      <p className="text-gray-500 font-mono-cyber text-xs uppercase tracking-widest">Se requiere HTTPS (TLS 1.2+)</p>
    </div>
  );

  if(geoStatus==='blocked') return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center text-center p-12">
      <style>{GLOBAL_STYLES}</style>
      <MapPin size={64} className="mb-6 text-red-500 animate-bounce"/>
      <h1 className="font-orbitron text-3xl text-red-500 mb-4 uppercase">Acceso Fuera de Rango</h1>
      <p className="text-gray-500 font-mono-cyber text-xs uppercase tracking-widest">Región autorizada: ANTIOQUIA</p>
    </div>
  );

  // ── MODAL HERRAMIENTAS ───────────────────────────────────────
  const herramientasModal = showHerramienta && (
    <HerramientasModal
      tool={showHerramienta}
      onClose={cerrarHerramienta}
      onRun={runHerramienta}
      onCheckPassword={checkPassword}
      loading={htLoading}
      result={htResult}
      error={htError}
      input={htInput}
      setInput={setHtInput}
    />
  );

  if(user&&showActividades&&actividadesToken) return (
    <ActividadesModule user={user} currentIp={currentIp} actividadesToken={actividadesToken} onClose={()=>setShowActividades(false)}/>
  );

  // ── DASHBOARD ──────────────────────────────────────────────
  if(user) return (
    <div className="min-h-screen bg-gray-950 cyber-grid font-rajdhani">
      <style>{GLOBAL_STYLES}</style>
      {/* NAVBAR */}
      <nav className="bg-black/95 border-b border-cyan-500/20 px-8 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <CiberBitLogo size={36}/>
          <span className="font-orbitron text-xl font-black text-white uppercase tracking-widest glow-cyan">CiberBit</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"/>
            <span className="text-green-400 text-[10px] font-mono-cyber uppercase">{user.email}</span>
          </div>
          <CyberButton variant="danger" onClick={()=>{setUser(null);setView('login');setActividadesToken(null);}} className="py-2 px-6">
            Cerrar Sesión
          </CyberButton>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto p-8">
        {/* HEADER */}
        <div className="text-center mb-10 pt-4">
          <p className="font-mono-cyber text-cyan-500/60 text-xs uppercase tracking-[0.4em] mb-2">// Acceso autorizado</p>
          <h1 className="font-orbitron text-4xl md:text-6xl font-black text-white uppercase leading-tight">
            Bienvenido, <span className="text-cyan-400 glow-cyan">{user.name.split(' ')[0]}</span>
          </h1>
          <div className="neon-line mt-6 max-w-md mx-auto"/>
        </div>

        {/* STATUS BAR */}
        <div className="bg-black/60 border border-cyan-500/20 px-6 py-3 flex flex-wrap gap-6 justify-center mb-8">
          <div className="flex items-center gap-2">
            <CheckCircle size={14} className="text-green-400"/>
            <span className="text-green-400 text-[11px] font-mono-cyber uppercase">2FA Activo</span>
          </div>
          <div className="flex items-center gap-2">
            <Shield size={14} className="text-cyan-400"/>
            <span className="text-cyan-400 text-[11px] font-mono-cyber uppercase">TLS 1.3</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-purple-400"/>
            <span className="text-purple-400 text-[11px] font-mono-cyber uppercase">IP: {currentIp}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-[11px] font-mono-cyber uppercase">Ley 1581: {user.policy||"SI"}</span>
          </div>
        </div>

        {/* MÓDULOS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
          {/* DORKING */}
          <div className="bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10 group p-8"
            style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,0 100%)'}}>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"/>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-cyan-500/50 flex items-center justify-center bg-cyan-500/5 group-hover:bg-cyan-500/10 transition-all">
                <Search size={20} className="text-cyan-400"/>
              </div>
              <div>
                <p className="font-mono-cyber text-[10px] text-cyan-500/60 uppercase tracking-widest">Módulo OSINT</p>
                <p className="font-orbitron text-sm font-black text-white uppercase">Dorking</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs font-rajdhani mb-6">Plataforma de inteligencia, análisis de superficie y búsqueda avanzada.</p>
            <button onClick={accederDorking}
              className="w-full py-3 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/50 hover:border-cyan-400 text-cyan-300 hover:text-white font-mono-cyber text-[11px] uppercase tracking-widest transition-all cursor-pointer flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(0,245,212,0.2)]">
              <Search size={14}/> Acceder a Dorking
            </button>
          </div>

          {/* ACTIVIDADES */}
          <div className="bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10 group p-8"
            style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,0 100%)'}}>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"/>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-purple-500/50 flex items-center justify-center bg-purple-500/5 group-hover:bg-purple-500/10 transition-all">
                <BookOpen size={20} className="text-purple-400"/>
              </div>
              <div>
                <p className="font-mono-cyber text-[10px] text-purple-500/60 uppercase tracking-widest">Recursos</p>
                <p className="font-orbitron text-sm font-black text-white uppercase">Actividades</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs font-rajdhani mb-6">Materiales formativos de ciberseguridad. Solo lectura, acceso auditado.</p>
            <button onClick={accederActividades} disabled={tokenLoading}
              className="w-full py-3 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/50 hover:border-purple-400 text-purple-300 hover:text-white font-mono-cyber text-[11px] uppercase tracking-widest transition-all cursor-pointer flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(168,85,247,0.2)] disabled:opacity-40">
              {tokenLoading?<Loader size={14} className="animate-spin"/>:<BookOpen size={14}/>}
              Actividades Ciberseguridad
            </button>
          </div>

          {/* ANALIZADOR URL/IP */}
          <div className="bg-gray-900/80 border border-green-500/20 hover:border-green-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/10 group p-8"
            style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,0 100%)'}}>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-green-500/50 to-transparent"/>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-green-500/50 flex items-center justify-center bg-green-500/5 group-hover:bg-green-500/10 transition-all">
                <Globe size={20} className="text-green-400"/>
              </div>
              <div>
                <p className="font-mono-cyber text-[10px] text-green-500/60 uppercase tracking-widest">VirusTotal</p>
                <p className="font-orbitron text-sm font-black text-white uppercase">URL / IP</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs font-rajdhani mb-6">Analiza URLs e IPs contra 70+ motores antivirus. Detecta malware, phishing y reputación.</p>
            <button onClick={()=>abrirHerramienta('url')}
              className="w-full py-3 bg-green-500/10 hover:bg-green-500/20 border border-green-500/50 hover:border-green-400 text-green-300 hover:text-white font-mono-cyber text-[11px] uppercase tracking-widest transition-all cursor-pointer flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(34,197,94,0.2)]">
              <Globe size={14}/> Analizar URL / IP
            </button>
          </div>

          {/* VERIFICADOR CONTRASEÑAS */}
          <div className="bg-gray-900/80 border border-yellow-500/20 hover:border-yellow-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/10 group p-8"
            style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,0 100%)'}}>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent"/>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-yellow-500/50 flex items-center justify-center bg-yellow-500/5 group-hover:bg-yellow-500/10 transition-all">
                <Key size={20} className="text-yellow-400"/>
              </div>
              <div>
                <p className="font-mono-cyber text-[10px] text-yellow-500/60 uppercase tracking-widest">HIBP · k-anonymity</p>
                <p className="font-orbitron text-sm font-black text-white uppercase">Contraseñas</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs font-rajdhani mb-6">Verifica si una contraseña fue filtrada. La clave nunca sale del navegador — solo el hash SHA-1.</p>
            <button onClick={()=>abrirHerramienta('password')}
              className="w-full py-3 bg-yellow-500/10 hover:bg-yellow-500/20 border border-yellow-500/50 hover:border-yellow-400 text-yellow-300 hover:text-white font-mono-cyber text-[11px] uppercase tracking-widest transition-all cursor-pointer flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(234,179,8,0.2)]">
              <Key size={14}/> Verificar Contraseña
            </button>
          </div>

          {/* VERIFICADOR EMAIL */}
          <div className="bg-gray-900/80 border border-red-500/20 hover:border-red-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/10 group p-8"
            style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,0 100%)'}}>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-red-500/50 to-transparent"/>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-red-500/50 flex items-center justify-center bg-red-500/5 group-hover:bg-red-500/10 transition-all">
                <AtSign size={20} className="text-red-400"/>
              </div>
              <div>
                <p className="font-mono-cyber text-[10px] text-red-500/60 uppercase tracking-widest">HIBP · Brechas</p>
                <p className="font-orbitron text-sm font-black text-white uppercase">Emails Filtrados</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs font-rajdhani mb-6">Consulta si un correo aparece en brechas de datos conocidas. Muestra fecha, tipo de datos y fuente.</p>
            <button onClick={()=>abrirHerramienta('email')}
              className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 border border-red-500/50 hover:border-red-400 text-red-300 hover:text-white font-mono-cyber text-[11px] uppercase tracking-widest transition-all cursor-pointer flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(239,68,68,0.2)]">
              <AtSign size={14}/> Verificar Email
            </button>
          </div>

          {/* OSINT USUARIO */}
          <div className="bg-gray-900/80 border border-orange-500/20 hover:border-orange-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-orange-500/10 group p-8"
            style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,0 100%)'}}>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-orange-500/50 to-transparent"/>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-orange-500/50 flex items-center justify-center bg-orange-500/5 group-hover:bg-orange-500/10 transition-all">
                <UserSearch size={20} className="text-orange-400"/>
              </div>
              <div>
                <p className="font-mono-cyber text-[10px] text-orange-500/60 uppercase tracking-widest">25 plataformas</p>
                <p className="font-orbitron text-sm font-black text-white uppercase">OSINT Usuario</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs font-rajdhani mb-6">Busca un username en Google, DuckDuckGo, Bing, GitHub, redes sociales, Shodan y más.</p>
            <button onClick={()=>abrirHerramienta('osint')}
              className="w-full py-3 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/50 hover:border-orange-400 text-orange-300 hover:text-white font-mono-cyber text-[11px] uppercase tracking-widest transition-all cursor-pointer flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(249,115,22,0.2)]">
              <UserSearch size={14}/> Buscar Usuario OSINT
            </button>
          </div>

          {/* ENCICLOPEDIA DE ATAQUES */}
          <div className="bg-gray-900/80 border border-blue-500/20 hover:border-blue-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/10 group p-8"
            style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,0 100%)'}}>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"/>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-blue-500/50 flex items-center justify-center bg-blue-500/5 group-hover:bg-blue-500/10 transition-all">
                <Shield size={20} className="text-blue-400"/>
              </div>
              <div>
                <p className="font-mono-cyber text-[10px] text-blue-500/60 uppercase tracking-widest">8 tipos de ataques</p>
                <p className="font-orbitron text-sm font-black text-white uppercase">Info Ataques</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs font-rajdhani mb-6">Enciclopedia informativa sobre tipos de ataques: DDoS, Phishing, Ransomware, SQLi y más. Fuentes verificadas.</p>
            <button onClick={()=>setShowInfo(true)}
              className="w-full py-3 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/50 hover:border-blue-400 text-blue-300 hover:text-white font-mono-cyber text-[11px] uppercase tracking-widest transition-all cursor-pointer flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(59,130,246,0.2)]">
              <Shield size={14}/> Ver Enciclopedia de Ataques
            </button>
          </div>

          {/* ANALIZADOR METADATOS */}
          <div className="bg-gray-900/80 border border-pink-500/20 hover:border-pink-500/60 transition-all duration-300 hover:shadow-lg hover:shadow-pink-500/10 group p-8"
            style={{clipPath:'polygon(0 0,calc(100% - 20px) 0,100% 20px,100% 100%,0 100%)'}}>
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-pink-500/50 to-transparent"/>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 border border-pink-500/50 flex items-center justify-center bg-pink-500/5 group-hover:bg-pink-500/10 transition-all">
                <Camera size={20} className="text-pink-400"/>
              </div>
              <div>
                <p className="font-mono-cyber text-[10px] text-pink-500/60 uppercase tracking-widest">EXIF · GPS · IPTC · XMP</p>
                <p className="font-orbitron text-sm font-black text-white uppercase">Metadatos</p>
              </div>
            </div>
            <p className="text-gray-500 text-xs font-rajdhani mb-6">Extrae metadatos ocultos de fotos y archivos: GPS, camara, fecha, autor, software. 100% local, sin subir nada.</p>
            <button onClick={()=>setShowMetadatos(true)}
              className="w-full py-3 bg-pink-500/10 hover:bg-pink-500/20 border border-pink-500/50 hover:border-pink-400 text-pink-300 hover:text-white font-mono-cyber text-[11px] uppercase tracking-widest transition-all cursor-pointer flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(236,72,153,0.2)]">
              <Camera size={14}/> Analizar Metadatos
            </button>
          </div>

        </div>

        {/* ACTIVIDAD RECIENTE */}
        <div className="bg-gray-900/60 border border-gray-800"
          style={{clipPath:'polygon(0 0,calc(100% - 16px) 0,100% 16px,100% 100%,0 100%)'}}>
          <div className="px-6 py-4 border-b border-gray-800 flex items-center gap-3">
            <RefreshCcw size={14} className="text-gray-500"/>
            <span className="font-mono-cyber text-[11px] text-gray-500 uppercase tracking-widest">Actividad Reciente</span>
          </div>
          <div className="p-4 space-y-2 max-h-48 overflow-y-auto">
            {user.logs&&user.logs.length>0?user.logs.map((log,i)=>(
              <div key={i} className="flex justify-between items-center text-[10px] bg-black/40 border border-gray-800 px-4 py-2.5 font-mono-cyber uppercase">
                <div>
                  <span className="text-gray-400">{log.fecha} {log.hora}</span>
                  <span className="text-gray-700 mx-2">·</span>
                  <span className="text-gray-600">IP: {log.ip}</span>
                </div>
                <span className={`px-2 py-1 text-[9px] ${log.estado.includes('SUCCESS')?'text-green-400 border border-green-500/30 bg-green-500/5':'text-red-400 border border-red-500/30 bg-red-500/5'}`}>
                  {log.estado}
                </span>
              </div>
            )):(
              <p className="text-gray-700 font-mono-cyber text-[10px] uppercase text-center py-4">Sin registros recientes</p>
            )}
          </div>
        </div>
      </main>
      {herramientasModal}
      {showInfo && <InfoPanel onClose={()=>setShowInfo(false)}/>}
      {showMetadatos && <MetadatosPanel onClose={()=>setShowMetadatos(false)}/>}
    </div>
  );

  if(requires2FA) return (
    <TwoFactorAuth email={tempEmail} currentIp={currentIp}
      onVerified={u=>{setUser(u);setRequires2FA(false);}}
      onCancel={()=>{setRequires2FA(false);setView('login');}}
      onLog={reportLog}/>
  );

  // ── LOGIN / REGISTRO ───────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-950 cyber-grid flex items-center justify-center p-6 select-none">
      <style>{GLOBAL_STYLES}</style>

      {/* SCANLINE decorativa */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden opacity-5 z-0">
        <div className="absolute w-full h-1 bg-cyan-400" style={{animation:'scanline 8s linear infinite'}}/>
      </div>

      <div className="max-w-md w-full relative z-10">
        {/* LOGO Y TÍTULO */}
        <div className="text-center mb-10">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <CiberBitLogo size={90}/>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 border border-cyan-500/20 rounded-full animate-ping" style={{animationDuration:'3s'}}/>
              </div>
            </div>
          </div>
          <h1 className="font-orbitron text-5xl font-black text-white uppercase tracking-widest glow-cyan flicker">CiberBit</h1>
          <p className="font-mono-cyber text-[11px] text-cyan-500/60 uppercase tracking-[0.5em] mt-3">Acceso Industrial Blindado</p>
          <div className="neon-line mt-4 mx-auto max-w-xs"/>
        </div>

        {/* LOGIN */}
        {view==='login'&&(
          <CyberCard title="Ingreso">
            <form onSubmit={handleLogin} className="space-y-1">
              <CyberInput label="Email Corporativo" name="email" placeholder="usuario@dominio.com" icon={Mail} required/>
              <CyberInput label="Clave Operativa" name="password" type="password" placeholder="••••••••" icon={Lock} preventPaste={true} required/>
              <div className="text-right pb-3">
                <button type="button" onClick={()=>setView('forgot')} className="text-[10px] font-mono-cyber text-gray-600 hover:text-cyan-400 uppercase underline transition-colors">¿Olvidó su clave?</button>
              </div>
              {errorMsg&&<p className="text-red-400 text-[10px] font-mono-cyber uppercase text-center pb-2">{String(errorMsg)}</p>}
              <CyberButton type="submit" className="w-full py-4" disabled={loading}>{loading?'Verificando...':'Acceder al Sistema'}</CyberButton>
              <div className="text-center pt-6">
                <button type="button" onClick={()=>setView('register')} className="text-[10px] font-mono-cyber text-gray-600 hover:text-cyan-400 uppercase underline transition-colors">Crear nueva cuenta</button>
              </div>
            </form>
          </CyberCard>
        )}

        {/* REGISTRO PASO 1 */}
        {view==='register'&&!verifyTokenSent&&!setupSecret&&(
          <CyberCard title="Registro">
            <form onSubmit={startRegistration} className="space-y-1">
              <CyberSelect label="Tipo Doc" name="docType" icon={FileText} options={["CC","CE","TI","NIT","PASAPORTE"]} required/>
              <CyberInput label="No. Documento" name="docNumber" icon={UserCheck} onChange={e=>e.target.value=e.target.value.replace(/[^0-9]/g,'')} inputMode="numeric" required/>
              <CyberInput label="Nombre" name="name" icon={User} onChange={e=>e.target.value=e.target.value.toUpperCase().replace(/[^A-ZÑÁÉÍÓÚ\s]/g,'')} required/>
              <CyberInput label="Email" name="email" type="email" icon={Mail} required/>
              <CyberInput label="Contacto" name="contact" icon={Smartphone} onChange={e=>e.target.value=e.target.value.replace(/[^0-9]/g,'')} inputMode="tel" required/>
              <CyberInput label="Contraseña" name="password" type="password" icon={Lock} preventPaste={true} required/>
              <CyberInput label="Confirmar" name="confirmPassword" type="password" icon={Lock} preventPaste={true} required/>
              {/* CAPTCHA */}
              <div className="bg-black/40 border border-gray-700 p-4 flex justify-between items-center mb-2">
                <div className="flex gap-2 items-center font-mono-cyber text-cyan-400">
                  <Calculator size={16}/><span className="font-bold">{captcha.n1} + {captcha.n2} =</span>
                </div>
                <input type="number" value={captchaIn} onChange={e=>setCaptchaIn(e.target.value)}
                  className="w-20 p-2 bg-gray-900 border border-gray-600 text-center font-mono-cyber text-white outline-none focus:border-cyan-500" required/>
              </div>
              {/* TERMS */}
              <div className="flex items-start gap-3 py-2">
                <input type="checkbox" id="terms" checked={termsAccepted} onChange={e=>setTermsAccepted(e.target.checked)} required
                  className="mt-1 w-4 h-4 accent-cyan-500 cursor-pointer shrink-0"/>
                <label htmlFor="terms" className="text-[10px] font-mono-cyber text-gray-500 uppercase leading-relaxed cursor-pointer">
                  Acepto los <button type="button" onClick={()=>setShowTermsModal(true)} className="text-cyan-400 underline hover:text-cyan-300">Términos y Ley 1581</button>
                </label>
                <input type="hidden" name="policyVersion" value="v1.0-2026"/>
                <input type="hidden" name="aceptoPoliticas" value={termsAccepted?"SI":"NO"}/>
              </div>
              {errorMsg&&<p className="text-red-400 text-[10px] font-mono-cyber uppercase text-center">{String(errorMsg)}</p>}
              <CyberButton type="submit" className="w-full py-4" disabled={loading||!termsAccepted}>Validar Correo</CyberButton>
              <button type="button" onClick={()=>setView('login')} className="w-full text-[10px] font-mono-cyber text-gray-600 hover:text-gray-400 uppercase underline text-center mt-3 transition-colors">Volver</button>
            </form>
          </CyberCard>
        )}

        {/* REGISTRO PASO 2 */}
        {view==='register'&&verifyTokenSent&&(
          <CyberCard title="Validar Email">
            <form onSubmit={completeRegistration} className="space-y-4 text-center">
              <p className="text-xs font-mono-cyber text-gray-500 uppercase">Código enviado al correo</p>
              <CyberInput label="Código" name="vcode" placeholder="######" icon={KeyRound} required/>
              {errorMsg&&<p className="text-red-400 text-[10px] font-mono-cyber uppercase">{String(errorMsg)}</p>}
              {successMsg&&<p className="text-green-400 text-[10px] font-mono-cyber uppercase">{String(successMsg)}</p>}
              <CyberButton type="submit" className="w-full py-4" disabled={loading}>Verificar Código</CyberButton>
            </form>
          </CyberCard>
        )}

        {/* SETUP 2FA */}
        {view==='register'&&setupSecret&&(
          <TwoFactorSetup secret={setupSecret} email={regData.email} onContinue={()=>{setView('login');setSetupSecret(null);}}/>
        )}

        {/* RECUPERAR */}
        {view==='forgot'&&(
          <CyberCard title="Recuperar Acceso">
            <form onSubmit={handleForgot} className="space-y-4">
              <CyberInput label="Email" name="email" type="email" icon={Mail} required/>
              {errorMsg&&<p className="text-red-400 text-[10px] font-mono-cyber uppercase">{String(errorMsg)}</p>}
              <CyberButton type="submit" className="w-full py-4" disabled={loading}>Solicitar Token</CyberButton>
              <button type="button" onClick={()=>setView('login')} className="w-full text-[10px] font-mono-cyber text-gray-600 hover:text-gray-400 uppercase underline text-center transition-colors">Cancelar</button>
            </form>
          </CyberCard>
        )}

        {/* NUEVA CLAVE */}
        {view==='reset'&&(
          <CyberCard title="Nueva Clave">
            <form onSubmit={handleUpdatePass} className="space-y-1">
              <CyberInput label="Email" name="email" type="email" icon={Mail} required/>
              <CyberInput label="Token" name="token" placeholder="######" icon={KeyRound} required/>
              <CyberInput label="Nueva Contraseña" name="newPassword" type="password" icon={Lock} preventPaste={true} required/>
              <div className="bg-black/40 border border-gray-700 p-4 flex justify-between items-center mb-2">
                <div className="flex gap-2 items-center font-mono-cyber text-cyan-400">
                  <Calculator size={16}/><span className="font-bold">{captcha.n1} + {captcha.n2} =</span>
                </div>
                <input type="number" value={captchaIn} onChange={e=>setCaptchaIn(e.target.value)}
                  className="w-20 p-2 bg-gray-900 border border-gray-600 text-center font-mono-cyber text-white outline-none focus:border-cyan-500" required/>
              </div>
              {errorMsg&&<p className="text-red-400 text-[10px] font-mono-cyber uppercase text-center">{String(errorMsg)}</p>}
              <CyberButton type="submit" className="w-full py-4" disabled={loading}>Actualizar Clave</CyberButton>
            </form>
          </CyberCard>
        )}

        {/* FOOTER */}
        <div className="text-center mt-6 flex justify-center gap-6">
          <span className="text-gray-700 text-[9px] font-mono-cyber uppercase">TLS 1.3</span>
          <span className="text-gray-700 text-[9px] font-mono-cyber uppercase">ISO 27001</span>
          <span className="text-gray-700 text-[9px] font-mono-cyber uppercase">Ley 1581</span>
          <span className="text-gray-700 text-[9px] font-mono-cyber uppercase">2FA</span>
        </div>
      </div>

      {showTermsModal&&(
        <TermsModal onClose={()=>setShowTermsModal(false)} onAccept={()=>{setTermsAccepted(true);setShowTermsModal(false);}}/>
      )}
    </div>
  );
}